<script setup>
import {
  computed,
  defineAsyncComponent,
  onMounted,
  onUnmounted,
  nextTick,
  ref,
  watch,
} from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import Avatar from '@/components/Avatar.vue'
import Button from '@/components/Button.vue'
import GalleryView from '@/components/GalleryView.vue'
import Modal from '@/components/Modal.vue'
import Select from '@/components/Form/Select.vue'
import { useAlert } from '@/composables/useToast'
import { getAssignableAgents, getInboxes } from '@/api/inboxes'
import { plainBotText, splitBotText, splitTextLinks } from '@/helpers/links'
import ChannelIcon from '@/components/ChannelIcon.vue'
import { getTags } from '@/api/tags'
import {
  rateConversation,
  requestBlock,
  getMessages,
  searchConversations,
  exportConversation,
  getDraft,
  saveDraft,
  deleteDraft,
  sendTyping,
} from '@/api/conversations'
import { getMonitoring } from '@/api/monitoring'
import { getContact } from '@/api/contacts'
import { createCableConsumer } from '@/helpers/actionCable'
import {
  getCannedResponses,
  createCannedResponse,
  updateCannedResponse,
  deleteCannedResponse,
} from '@/api/cannedResponses'

// Async: keeps the heavy emoji catalog out of the main bundle
const EmojiPicker = defineAsyncComponent(
  () => import('@/components/emoji/EmojiPicker.vue')
)

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

// Realtime over WebSocket is temporarily disabled while client-side issues are
// investigated; flip to true to bring it back (polling then relaxes to 60s).
const REALTIME_ENABLED = false
const POLL_INTERVAL = REALTIME_ENABLED ? 60000 : 2000
let pollTimer = null

const replyText = ref('')
const threadRef = ref(null)
const stateTab = ref('active')
const onlyMine = ref(false)
const inboxFilter = ref('')
// Personal counters and tab counts; polled once in the app shell
const stats = computed(() => store.getters['stats/getStats'])

const currentUser = computed(() => store.getters['auth/getCurrentUser'])
const hasPermission = computed(() => store.getters['auth/hasPermission'])
const canSeeAll = computed(() => hasPermission.value('conversations_all'))
const canBlockDirectly = computed(() => hasPermission.value('blocklist_manage'))
const canManageSharedCanned = computed(() =>
  hasPermission.value('canned_shared_manage')
)

const conversations = computed(() => store.getters['conversations/getConversations'])
const selectedId = computed(() => store.getters['conversations/getSelectedId'])
const selectedConversation = computed(
  () => store.getters['conversations/getSelectedConversation']
)
// Each source presents its own bot; the i18n name is only the fallback
const botName = computed(
  () => selectedConversation.value?.inbox?.bot_name || t('CONVERSATIONS.BOT_NAME')
)
const uiFlags = computed(() => store.getters['conversations/getUIFlags'])
const messages = computed(() =>
  selectedId.value
    ? store.getters['conversations/getMessages'](selectedId.value)
    : []
)

const MEDIA_TYPES = ['image', 'video', 'audio']
const galleryStartId = ref(null)

// Media of the whole thread, so the gallery can be paged through
const galleryItems = computed(() =>
  messages.value.flatMap(message =>
    (message.attachments || [])
      .filter(attachment => MEDIA_TYPES.includes(attachment.file_type))
      .map(attachment => ({
        id: attachment.id,
        dataUrl: attachment.data_url,
        fileName: attachment.file_name,
        fileType: attachment.file_type,
        senderName: message.sender?.name || '',
        createdAt: message.created_at,
      }))
  )
)

const scopedConversations = computed(() => {
  let list = conversations.value
  if (inboxFilter.value)
    list = list.filter(c => c.inbox?.id === Number(inboxFilter.value))
  // Operators get an already-scoped list from the backend
  if (!canSeeAll.value || !onlyMine.value) return list
  return list.filter(c => c.assignee?.id === currentUser.value?.id)
})

const toOptions = (records, emptyLabel) => [
  { value: '', label: emptyLabel },
  ...records.map(record => ({ value: record.id, label: record.name })),
]

// Sources the operator actually works with; a single one needs no filter
const inboxes = ref([])
const inboxOptions = computed(() =>
  toOptions(inboxes.value, t('CONVERSATIONS.INBOX_FILTER.ALL'))
)

const loadInboxes = async () => {
  if (!accountId.value) return
  try {
    const { data } = await getInboxes(accountId.value)
    inboxes.value = data
  } catch (error) {
    inboxes.value = []
  }
}

// The list holds only recent closed threads, so counts come from the server.
// Under a source or "only mine" filter, count what is on screen instead.
const stateCounts = computed(() => {
  if (onlyMine.value || inboxFilter.value) return null

  return stats.value?.state_counts || null
})

const matchesState = (conversation, key) => {
  if (key === 'active')
    return (
      conversation.state === 'waiting_agent' ||
      conversation.state === 'waiting_client'
    )
  return conversation.state === key
}

// Operators live in two tabs; supervisors also see the queue breakdown
const stateTabs = computed(() => {
  const keys = canSeeAll.value
    ? ['active', 'queued', 'bot', 'waiting_agent', 'waiting_client', 'resolved']
    : ['active', 'resolved']
  return keys.map(key => ({
    key,
    // Short caption fits the equal-width segments, the full one is a tooltip
    label: t(`CONVERSATIONS.STATES.SHORT.${key.toUpperCase()}`),
    title: t(`CONVERSATIONS.STATES.${key.toUpperCase()}`),
    count:
      stateCounts.value?.[key] ??
      scopedConversations.value.filter(c => matchesState(c, key)).length,
  }))
})

const visibleConversations = computed(() =>
  scopedConversations.value.filter(c => matchesState(c, stateTab.value))
)

const previewText = message =>
  message?.generated_by_bot ? plainBotText(message.content) : message?.content

// Only the bot writes markdown: the client's own asterisks stay as typed
// A keycap without the variation selector (1⃣ as VK sends it) is not an emoji
// sequence to Firefox, which then draws the two halves apart
const keycapSafe = text =>
  (text || '').replace(/([0-9#*])\u20E3/g, '$1\uFE0F\u20E3')

const messageParts = message =>
  message.generated_by_bot
    ? splitBotText(keycapSafe(message.content))
    : splitTextLinks(keycapSafe(message.content))

// Name above the first message of a run, the way messengers do it
const senderLabel = index => {
  const message = messages.value[index]
  if (message?.generated_by_bot) return botName.value

  const name = message?.sender?.name
  if (!name || message.message_type === 'activity') return null

  for (let i = index - 1; i >= 0; i -= 1) {
    const prev = messages.value[i]
    if (prev.message_type === 'activity') continue
    const sameAuthor =
      prev.sender?.type === message.sender.type &&
      prev.sender?.id === message.sender.id
    return sameAuthor ? null : name
  }
  return name
}

const replyQuoteAuthor = quoted =>
  quoted?.sender_name || t('CONVERSATIONS.UNKNOWN')

const replyQuotePreview = quoted => {
  if (!quoted) return ''
  return (
    (quoted.content || '').replace(/\s+/g, ' ').trim() ||
    t('CONVERSATIONS.ATTACHMENTS.PREVIEW')
  )
}

const timeFormatter = new Intl.DateTimeFormat('ru', {
  hour: '2-digit',
  minute: '2-digit',
})
const dayFormatter = new Intl.DateTimeFormat('ru', {
  day: '2-digit',
  month: 'short',
})

// Day separators inside a thread: a conversation can span several days
const dateFormatter = new Intl.DateTimeFormat('ru', {
  day: 'numeric',
  month: 'long',
})
const dateWithYearFormatter = new Intl.DateTimeFormat('ru', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const dayLabel = value => {
  const date = new Date(value)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (date.toDateString() === today.toDateString())
    return t('CONVERSATIONS.DATE.TODAY')
  if (date.toDateString() === yesterday.toDateString())
    return t('CONVERSATIONS.DATE.YESTERDAY')
  return date.getFullYear() === today.getFullYear()
    ? dateFormatter.format(date)
    : dateWithYearFormatter.format(date)
}

// A caption per day, each sticky inside its own group: in one flat list they all
// pin to the same spot and pile up
const messageGroups = computed(() => {
  const groups = []
  messages.value.forEach((message, index) => {
    const day = new Date(message.created_at).toDateString()
    const current = groups[groups.length - 1]
    if (current?.day === day) current.items.push({ message, index })
    else
      groups.push({
        day,
        label: dayLabel(message.created_at),
        items: [{ message, index }],
      })
  })
  return groups
})

const formatTime = value => (value ? timeFormatter.format(new Date(value)) : '')
const formatListTime = value => {
  if (!value) return ''
  const date = new Date(value)
  const isToday = new Date().toDateString() === date.toDateString()
  return isToday ? timeFormatter.format(date) : dayFormatter.format(date)
}

// Distance from the bottom that still counts as "reading the newest messages"
const STICK_THRESHOLD = 80

const atBottom = ref(true)
const hasNewBelow = ref(false)

// Scrolls only while the operator is at the bottom, so reading history is not
// interrupted by the poll; force is for opening a thread and for own replies.
const scrollToBottom = ({ force = false } = {}) => {
  if (!force && !atBottom.value) return

  nextTick(() => {
    if (!threadRef.value) return

    threadRef.value.scrollTop = threadRef.value.scrollHeight
    atBottom.value = true
    hasNewBelow.value = false
  })
}

const onThreadScroll = () => {
  // Scrolling the thread means the operator is reading, not picking an emoji
  emojiOpen.value = false
  const element = threadRef.value
  if (!element) return

  atBottom.value =
    element.scrollHeight - element.scrollTop - element.clientHeight <
    STICK_THRESHOLD
  if (atBottom.value) hasNewBelow.value = false
}

const selectConversation = async (id, { keepPosition = false } = {}) => {
  hidePreview()
  nextTick(autoGrow)
  if (id === selectedId.value) return
  store.dispatch('conversations/select', id)
  await store.dispatch('conversations/fetchMessages', id)
  // Reading resets the unread badge server-side; refresh the list to show it
  store.dispatch('conversations/get')
  // A thread opened by a filter scrolls to what was found, not to its end
  if (!keepPosition) scrollToBottom({ force: true })
}

// The message the reply quotes; cleared after sending or on Esc
const replyTo = ref(null)
// The message being edited; mutually exclusive with quoting
const editingMessage = ref(null)
let textBeforeEdit = ''
const highlightedMessageId = ref(null)

// Attachments go straight into the draft, so nothing is uploaded twice
const draftFiles = ref([])
const draftSaving = ref(false)
// While the draft is loading a queued save would wipe it
const draftLoading = ref(false)
let draftTimer = null

const DRAFT_DELAY = 600

const loadDraft = async conversationId => {
  if (!accountId.value || !conversationId) return
  draftLoading.value = true
  try {
    const { data } = await getDraft(accountId.value, conversationId)
    // The operator may have switched again while the draft was loading
    if (selectedId.value !== conversationId) return
    replyText.value = data.content || ''
    draftFiles.value = data.files || []
    nextTick(autoGrow)
  } catch (error) {
    draftFiles.value = []
  } finally {
    draftLoading.value = false
  }
}

const persistDraft = async (
  conversationId,
  text = replyText.value,
  hasFiles = draftFiles.value.length > 0
) => {
  if (!accountId.value || !conversationId) return
  try {
    if (text.trim() || hasFiles) {
      await saveDraft(accountId.value, conversationId, { content: text })
    } else {
      await deleteDraft(accountId.value, conversationId)
    }
  } catch (error) {
    // a draft is a convenience, a failed save must not block the operator
  }
}

const scheduleDraftSave = () => {
  const conversationId = selectedId.value
  if (!conversationId || draftLoading.value || editingMessage.value) return
  clearTimeout(draftTimer)
  draftTimer = setTimeout(() => persistDraft(conversationId), DRAFT_DELAY)
}

const uploadDraftFiles = async files => {
  const conversationId = selectedId.value
  if (!conversationId || !files.length) return
  draftSaving.value = true
  try {
    const payload = new FormData()
    payload.append('content', replyText.value)
    files.forEach(file => payload.append('files[]', file))
    const { data } = await saveDraft(accountId.value, conversationId, payload)
    draftFiles.value = data.files || []
  } catch (error) {
    useAlert(t('CONVERSATIONS.DRAFT.FILE_ERROR'))
  } finally {
    draftSaving.value = false
  }
}

const removeDraftFile = async file => {
  const conversationId = selectedId.value
  try {
    await deleteDraft(accountId.value, conversationId, { file_id: file.id })
    draftFiles.value = draftFiles.value.filter(item => item.id !== file.id)
  } catch (error) {
    useAlert(t('CONVERSATIONS.DRAFT.FILE_ERROR'))
  }
}
const fileInput = ref(null)

const addFiles = fileList => {
  uploadDraftFiles(Array.from(fileList))
}

const onFilePick = event => {
  addFiles(event.target.files)
  event.target.value = ''
}

const removeFile = file => {
  removeDraftFile(file)
}

const onPaste = event => {
  const files = event.clipboardData?.files
  if (files?.length) {
    event.preventDefault()
    addFiles(files)
  }
}

const onDrop = event => {
  if (event.dataTransfer?.files?.length) addFiles(event.dataTransfer.files)
}

// The composer grows with the text up to a comfortable ceiling
const COMPOSER_MIN_HEIGHT = 96
const COMPOSER_MAX_HEIGHT = 260

const autoGrow = () => {
  const el = replyInput.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${Math.min(Math.max(el.scrollHeight, COMPOSER_MIN_HEIGHT), COMPOSER_MAX_HEIGHT)}px`
}

// Only the web widget shows the client that somebody is writing; in the
// messengers the flag would travel nowhere.
const pingTyping = () => {
  const account = currentUser.value?.accounts?.[0]?.id
  if (!selectedId.value || !account || !replyText.value.trim()) return
  if (selectedConversation.value?.inbox?.channel_slug !== 'web_widget') return

  sendTyping(account, selectedId.value).catch(() => {})
}

// The flag lives eight seconds server-side, so it is refreshed, not resent
const TYPING_PING_INTERVAL = 3000
let lastTypingPingAt = 0
const throttledPingTyping = () => {
  if (Date.now() - lastTypingPingAt < TYPING_PING_INTERVAL) return

  lastTypingPingAt = Date.now()
  pingTyping()
}

watch(replyText, () => {
  nextTick(autoGrow)
  scheduleDraftSave()
  throttledPingTyping()
})

// Spec 3.1.4 keeps Enter as a new line; each operator picks the sending hotkey
const sendHotKey = computed(
  () => currentUser.value?.ui_settings?.editor_message_key || 'cmd_enter'
)

const onReplyKeydown = event => {
  if (event.key !== 'Enter' || event.isComposing) return
  const withModifier = event.metaKey || event.ctrlKey
  const shouldSend =
    sendHotKey.value === 'enter'
      ? !withModifier && !event.shiftKey
      : withModifier
  if (!shouldSend) return

  event.preventDefault()
  submitComposer()
}

const sendHotKeyHint = computed(() =>
  sendHotKey.value === 'enter'
    ? t('CONVERSATIONS.REPLY.HOTKEY_ENTER')
    : t('CONVERSATIONS.REPLY.HOTKEY_CMD_ENTER')
)

const quotePreview = message => {
  if (message.content) return message.content
  return message.has_attachments || message.attachments?.length
    ? t('CONVERSATIONS.ATTACHMENTS.FILE')
    : ''
}

const startReply = message => {
  if (editingMessage.value) cancelEdit()
  replyTo.value = message
  replyInput.value?.focus()
}

const startEdit = message => {
  replyTo.value = null
  // The reply in progress comes back when the edit is cancelled
  textBeforeEdit = editingMessage.value ? textBeforeEdit : replyText.value
  editingMessage.value = message
  replyText.value = message.content
  nextTick(() => {
    autoGrow()
    replyInput.value?.focus()
  })
}

const cancelEdit = () => {
  if (!editingMessage.value) return

  editingMessage.value = null
  replyText.value = textBeforeEdit
  textBeforeEdit = ''
  nextTick(autoGrow)
}

const canEdit = message =>
  message.editable && message.sender?.id === currentUser.value?.id

const submitEdit = async () => {
  const content = replyText.value.trim()
  const message = editingMessage.value
  if (!content) return

  try {
    await store.dispatch('conversations/editMessage', {
      id: selectedId.value,
      messageId: message.id,
      content,
    })
    editingMessage.value = null
    replyText.value = textBeforeEdit
    textBeforeEdit = ''
    nextTick(autoGrow)
  } catch (error) {
    // The channel refused: the text stays as the client sees it
    useAlert(
      error.response?.data?.error || t('CONVERSATIONS.EDIT.ERROR')
    )
  }
}

// Jumps to the quoted message and marks it, so a long thread stays navigable
const scrollToMessage = id => {
  const el = document.getElementById(`message-${id}`)
  if (!el) return

  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  highlightedMessageId.value = id
  setTimeout(() => {
    if (highlightedMessageId.value === id) highlightedMessageId.value = null
  }, 1600)
}

// A note goes to colleagues, never to the client: quoting and editing are
// about the client's thread, so they are dropped when the mode is switched on
const noteMode = ref(false)
// Kept apart from the reply: a note must not overwrite the draft answer
const noteText = ref('')

const composerText = computed({
  get: () => (noteMode.value ? noteText.value : replyText.value),
  set: value => {
    if (noteMode.value) noteText.value = value
    else replyText.value = value
  },
})

watch(noteMode, on => {
  nextTick(autoGrow)
  if (!on) return

  replyTo.value = null
  editingMessage.value = null
})

watch(noteText, () => nextTick(autoGrow))

// К заметке вложений не приложить, поэтому её решает только текст
const composerEmpty = computed(() =>
  noteMode.value
    ? !noteText.value.trim()
    : !replyText.value.trim() && !draftFiles.value.length
)

const submitComposer = () => {
  if (editingMessage.value) return submitEdit()

  return noteMode.value ? sendNote() : sendReply()
}

const sendNote = async () => {
  const content = noteText.value.trim()
  if (!content || !selectedId.value) return

  noteText.value = ''
  try {
    await store.dispatch('conversations/sendMessage', {
      id: selectedId.value,
      content,
      note: true,
    })
    scrollToBottom({ force: true })
  } catch (error) {
    noteText.value = content
    useAlert(t('CONVERSATIONS.REPLY.ERROR'))
  }
}

const sendReply = async () => {
  const content = replyText.value.trim()
  const files = draftFiles.value
  if ((!content && !files.length) || !selectedId.value) return

  // A pending save must not resurrect the draft after it has been sent
  clearTimeout(draftTimer)
  const quoted = replyTo.value
  replyText.value = ''
  draftFiles.value = []
  replyTo.value = null
  try {
    await store.dispatch('conversations/sendMessage', {
      id: selectedId.value,
      content,
      replyToId: quoted?.id,
    })
    scrollToBottom({ force: true })
  } catch (error) {
    replyText.value = content
    draftFiles.value = files
    replyTo.value = quoted
    useAlert(t('CONVERSATIONS.REPLY.ERROR'))
  }
}

// Operators forget to tag a thread before closing it; the source decides
// whether they are reminded, and the reminder never blocks the closing
const tagPromptOpen = ref(false)

// The button is locked until the server answers: a second click used to flip
// the thread straight back open
const changingStatus = ref(false)

const toggleStatus = async () => {
  if (!selectedId.value || changingStatus.value) return
  const wasOpen = selectedConversation.value?.status !== 'resolved'
  const asksForTag =
    selectedConversation.value?.inbox?.ask_tag_on_resolve &&
    !selectedConversation.value?.tag
  changingStatus.value = true
  try {
    await store.dispatch('conversations/setStatus', {
      id: selectedId.value,
      status: wasOpen ? 'resolved' : 'open',
    })
    // Search results are a snapshot: the "closed" chip there has to follow too
    const found = searchResults.value.find(c => c.id === selectedId.value)
    if (found) found.status = wasOpen ? 'resolved' : 'open'
    if (!wasOpen || !asksForTag) return

    promptTagId.value = ''
    tagPromptOpen.value = true
  } catch (error) {
    useAlert(t('CONVERSATIONS.STATUS.ERROR'))
  } finally {
    changingStatus.value = false
  }
}

const tags = ref([])
const tagMenuOpen = ref(false)
const promptTagId = ref('')

const tagPromptOptions = computed(() =>
  tags.value.map(tag => ({ value: tag.id, label: tag.name }))
)
const settingTag = ref(false)

const loadTags = async () => {
  const accountId = currentUser.value?.accounts?.[0]?.id
  if (!accountId) return
  try {
    const { data } = await getTags(accountId)
    tags.value = data
  } catch (error) {
    tags.value = []
  }
}

const setTag = async tagId => {
  tagMenuOpen.value = false
  tagPromptOpen.value = false
  if (settingTag.value) return
  settingTag.value = true
  try {
    await store.dispatch('conversations/setTag', {
      id: selectedId.value,
      tagId,
    })
  } catch (error) {
    useAlert(t('CONVERSATIONS.TAG.ERROR'))
  } finally {
    settingTag.value = false
  }
}

const cannedResponses = ref([])
const cannedOpen = ref(false)
const cannedQuery = ref('')
const cannedFormOpen = ref(false)
const cannedForm = ref({ id: null, title: '', content: '', shared: false })
const savingCanned = ref(false)

const accountId = computed(() => currentUser.value?.accounts?.[0]?.id)

const filteredCanned = computed(() => {
  const query = cannedQuery.value.trim().toLowerCase()
  if (!query) return cannedResponses.value
  return cannedResponses.value.filter(
    r =>
      r.title.toLowerCase().includes(query) ||
      r.content.toLowerCase().includes(query)
  )
})

const loadCannedResponses = async () => {
  if (!accountId.value) return
  try {
    const { data } = await getCannedResponses(accountId.value)
    cannedResponses.value = data
  } catch (error) {
    cannedResponses.value = []
  }
}

const toggleCanned = () => {
  cannedOpen.value = !cannedOpen.value
  cannedQuery.value = ''
  cannedFormOpen.value = false
  emojiOpen.value = false
  if (cannedOpen.value) loadCannedResponses()
}

const emojiOpen = ref(false)
const replyInput = ref(null)
// The wrapped line count depends on the width, and on a cold page load the
// composer is measured before the layout settles
let composerObserver = null
let composerWidth = 0

watch(replyInput, el => {
  composerObserver?.disconnect()
  composerObserver = null
  if (!el) return

  composerObserver = new ResizeObserver(([entry]) => {
    const { width } = entry.contentRect
    if (width === composerWidth) return
    composerWidth = width
    autoGrow()
  })
  composerObserver.observe(el)
})


const toggleEmoji = () => {
  emojiOpen.value = !emojiOpen.value
  cannedOpen.value = false
}

// Insert the emoji at the caret and keep the caret right after it
const insertEmoji = emoji => {
  const el = replyInput.value
  const start = el?.selectionStart ?? replyText.value.length
  const end = el?.selectionEnd ?? start
  replyText.value =
    replyText.value.slice(0, start) + emoji + replyText.value.slice(end)
  emojiOpen.value = false
  nextTick(() => {
    el?.focus()
    el?.setSelectionRange(start + emoji.length, start + emoji.length)
  })
}

// Insert at the caret, the same way emoji are inserted
const insertCanned = response => {
  const el = replyInput.value
  const start = el?.selectionStart ?? replyText.value.length
  const end = el?.selectionEnd ?? start
  replyText.value =
    replyText.value.slice(0, start) + response.content + replyText.value.slice(end)
  cannedOpen.value = false
  nextTick(() => {
    const caret = start + response.content.length
    el?.focus()
    el?.setSelectionRange(caret, caret)
  })
}

// One form for both creating and editing, like in the directory
const openCannedForm = (response = null) => {
  cannedForm.value = response
    ? { ...response }
    : { id: null, title: '', content: '', shared: false }
  cannedFormOpen.value = true
}

const submitCanned = async () => {
  const { id, title, content, shared } = cannedForm.value
  if (!title.trim() || !content.trim() || savingCanned.value) return
  savingCanned.value = true
  try {
    if (id) await updateCannedResponse(accountId.value, id, { title, content })
    else await createCannedResponse(accountId.value, { title, content, shared })
    cannedForm.value = { id: null, title: '', content: '', shared: false }
    cannedFormOpen.value = false
    await loadCannedResponses()
  } catch (error) {
    useAlert(t('CONVERSATIONS.CANNED.ERROR'))
  } finally {
    savingCanned.value = false
  }
}

const cannedDeleteTarget = ref(null)

const confirmCannedDelete = async () => {
  try {
    await deleteCannedResponse(accountId.value, cannedDeleteTarget.value.id)
    cannedDeleteTarget.value = null
    await loadCannedResponses()
  } catch (error) {
    useAlert(t('CONVERSATIONS.CANNED.ERROR'))
  }
}

// Own responses are editable by the author, shared ones need the permission
const canEditCanned = response =>
  !response.shared || canManageSharedCanned.value

const openRatingDetails = ref(null)
const toggleRatingDetails = threadId => {
  openRatingDetails.value = openRatingDetails.value === threadId ? null : threadId
}

const ratingByThread = computed(() =>
  Object.fromEntries(
    (selectedConversation.value?.ratings || []).map(item => [
      item.thread_id,
      item,
    ])
  )
)

// The chip closes a rated stretch, so it sits after its last message
// Chips hang on the stretch's last bubble: a closing note or a brief has
// no bubble to hang them on
const BUBBLES = ['incoming', 'outgoing']
const lastInThread = computed(() => {
  const map = {}
  messages.value.forEach(message => {
    if (message.thread_id && BUBBLES.includes(message.message_type))
      map[message.thread_id] = message.id
  })
  return map
})

const ratingChip = message =>
  lastInThread.value[message.thread_id] === message.id
    ? ratingByThread.value[message.thread_id]
    : null

// The client's mark on the same stretch, read-only: operators do not edit it
// The client's mark hangs where they gave it: on the last bubble of its stretch
// written before the answer, not at the far end of a talk that went on
const clientMarkByMessage = computed(() => {
  const map = {}
  ;(selectedConversation.value?.client_marks || []).forEach(mark => {
    const answeredAt = new Date(mark.answered_at)
    const before = messages.value.filter(
      message =>
        message.thread_id === mark.thread_id &&
        BUBBLES.includes(message.message_type) &&
        new Date(message.created_at) <= answeredAt
    )
    const anchor = before.length
      ? before[before.length - 1].id
      : lastInThread.value[mark.thread_id]
    if (anchor) map[anchor] = mark
  })
  return map
})
const clientMark = message => clientMarkByMessage.value[message.id] || null
const CLIENT_FACES = ['😡', '😕', '😐', '🙂', '😍']
const openClientMark = ref(null)
const toggleClientMark = threadId => {
  openClientMark.value = openClientMark.value === threadId ? null : threadId
}

// The stretch still waiting for a rating: the last message's segment, unless
// it is rated already
const openThreadId = computed(() => {
  const last = messages.value[messages.value.length - 1]
  const id = last?.thread_id
  return id && !ratingByThread.value[id] ? id : null
})

const openThreadMessages = computed(() =>
  messages.value.filter(m => m.thread_id === openThreadId.value)
)

// The marker goes before the first message of the open stretch — the only
// border the operator cannot see otherwise, old dialogs start it mid-thread
const opensRatedStretch = message =>
  canRate.value &&
  openThreadId.value &&
  openThreadMessages.value[0]?.id === message.id

const ratingScope = computed(() => {
  const first = openThreadMessages.value[0]
  return {
    time: first ? formatTime(first.created_at) : '',
    count: openThreadMessages.value.filter(m => m.generated_by_bot).length,
  }
})

const showRatingModal = ref(false)
const rating = ref(5)
const ratingMistake = ref('')
const ratingCorrection = ref('')
const savingRating = ref(false)
const RATING_SCALE = [1, 2, 3, 4, 5]

// The bot has to have spoken in the open stretch, otherwise there is nothing to rate
const canRate = computed(() => selectedConversation.value?.can_rate === true)

const openRating = () => {
  rating.value = 5
  ratingMistake.value = ''
  ratingCorrection.value = ''
  showRatingModal.value = true
}

const submitRating = async () => {
  if (savingRating.value) return
  savingRating.value = true
  try {
    await rateConversation(accountId.value, selectedId.value, {
      rating: rating.value,
      mistake: ratingMistake.value.trim(),
      correction: ratingCorrection.value.trim(),
    })
    useAlert(t('CONVERSATIONS.RATING.SUCCESS'))
    showRatingModal.value = false
    await selectConversation(selectedId.value)
  } catch (error) {
    useAlert(t('CONVERSATIONS.RATING.ERROR'))
  } finally {
    savingRating.value = false
  }
}

const showBlockModal = ref(false)
const blocking = ref(false)

const submitBlock = async () => {
  if (blocking.value) return
  blocking.value = true
  try {
    await requestBlock(accountId.value, selectedId.value)
    useAlert(
      canBlockDirectly.value
        ? t('CONVERSATIONS.BLOCK.DONE')
        : t('CONVERSATIONS.BLOCK.REQUESTED')
    )
    showBlockModal.value = false
  } catch (error) {
    useAlert(t('CONVERSATIONS.BLOCK.ERROR'))
  } finally {
    blocking.value = false
  }
}

// Client card: profile and full conversation history (spec 3.1.6)
const contactCardOpen = ref(false)
const contactCard = ref(null)

const loadContactCard = async () => {
  if (!accountId.value || !selectedConversation.value) return
  try {
    const { data } = await getContact(
      accountId.value,
      selectedConversation.value.contact.id
    )
    contactCard.value = data
  } catch (error) {
    useAlert(t('CONTACT_CARD.ERROR'))
  }
}

// Spec 3.4: download the transcript as an Excel-friendly CSV
const downloadTranscript = async () => {
  try {
    const { data } = await exportConversation(accountId.value, selectedId.value)
    const url = URL.createObjectURL(data)
    const link = document.createElement('a')
    link.href = url
    link.download = `dialog-${selectedConversation.value.display_id}.csv`
    link.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    useAlert(t('CONVERSATIONS.EXPORT.ERROR'))
  }
}

const toggleContactCard = () => {
  contactCardOpen.value = !contactCardOpen.value
  if (contactCardOpen.value) loadContactCard()
}

// The card follows the client: a thread opened from search or from the card
// itself arrives a moment later, and the card would stay loading forever
watch(
  () => selectedConversation.value?.contact?.id,
  contactId => {
    if (!contactId || !contactCardOpen.value) return

    // The same client whose thread was still loading: refresh in place, only
    // a different person is worth wiping the panel for
    if (contactCard.value?.id !== contactId) contactCard.value = null
    loadContactCard()
  }
)

watch(selectedId, (id, previousId) => {
  // Flush the draft before clearing, or the text typed last is lost
  clearTimeout(draftTimer)
  if (previousId) {
    persistDraft(previousId, replyText.value, draftFiles.value.length > 0)
  }
  replyText.value = ''
  noteText.value = ''
  draftFiles.value = []
  replyTo.value = null
  editingMessage.value = null
  textBeforeEdit = ''
  if (id) loadDraft(id)

  // Another thread of the same client: refresh the history in place, the card
  // is already showing the right person and must keep its scroll
  const shown = contactCard.value?.id
  if (shown && shown === selectedConversation.value?.contact?.id) loadContactCard()
})

// The open dialog and the tab live in the url, so a page refresh (or a link
// sent to a colleague) lands on the same place
const syncLocation = () => {
  const query = { ...route.query }
  if (selectedId.value) query.c = String(selectedId.value)
  else delete query.c
  if (stateTab.value === 'active') delete query.tab
  else query.tab = stateTab.value
  router.replace({ query })
}

watch([selectedId, stateTab], syncLocation)


const restoreFromQuery = () => {
  const { c, tab } = route.query
  if (tab && stateTabs.value.some(item => item.key === tab)) stateTab.value = tab
  if (c) selectConversation(Number(c))
}

// Arrow keys walk the list while the focus is on it, like in mail clients
const listRef = ref(null)

const moveSelection = step => {
  const list = visibleConversations.value
  if (!list.length) return

  const current = list.findIndex(c => c.id === selectedId.value)
  const next = current === -1 ? 0 : current + step
  const conversation = list[next]
  if (!conversation) return

  selectConversation(conversation.id)
  nextTick(() => {
    const button = listRef.value?.querySelector(
      `[data-conversation="${conversation.id}"]`
    )
    button?.focus()
    button?.scrollIntoView({ block: 'nearest' })
  })
}

// Operators quote these ids to the client and to each other, so one click copies
const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(String(value))
    useAlert(t('CONVERSATIONS.COPIED', { label }))
  } catch (error) {
    useAlert(t('CONVERSATIONS.COPY_ERROR'))
  }
}

const sinceFormatter = new Intl.DateTimeFormat('ru', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
})

// Spec 3.1.7: search by text, thread number or client with period and tag filters
const searchOpen = ref(false)
const searchQuery = ref('')
const searchTagId = ref('')
const searchAgentId = ref('')
const searchStatus = ref('')
const searchBot = ref('')
const searchSince = ref('')
const searchUntil = ref('')
const searchResults = ref([])
const searchTotal = ref(0)
const searchHasMore = ref(false)
const searching = ref(false)
const searchDone = ref(false)
let searchTimer = null

// Filters are personal, so they live in the browser instead of the url
const FILTERS_KEY = 'mkChat.conversationFilters'

const filterRefs = {
  q: searchQuery,
  inbox: inboxFilter,
  tag_id: searchTagId,
  agent_id: searchAgentId,
  status: searchStatus,
  bot: searchBot,
  since: searchSince,
  until: searchUntil,
}

const saveFilters = () => {
  const stored = Object.fromEntries(
    Object.entries(filterRefs)
      .map(([key, item]) => [key, item.value])
      .filter(([, value]) => value !== '' && value !== null)
  )
  if (Object.keys(stored).length) {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(stored))
  } else {
    localStorage.removeItem(FILTERS_KEY)
  }
}

const restoreFilters = () => {
  let stored = {}
  try {
    stored = JSON.parse(localStorage.getItem(FILTERS_KEY)) || {}
  } catch (error) {
    stored = {}
  }
  // A link from monitoring wins over what the browser remembers
  const fromQuery = ['agent_id', 'since', 'until', 'inbox', 'status'].some(
    key => route.query[key]
  )
  if (fromQuery) return false

  Object.entries(filterRefs).forEach(([key, item]) => {
    if (stored[key] === undefined) return
    item.value = key === 'inbox' ? Number(stored[key]) : stored[key]
  })
  return Object.keys(stored).length > 0
}

const hasSearchInput = computed(
  () =>
    Boolean(searchQuery.value.trim()) ||
    Boolean(inboxFilter.value) ||
    Boolean(searchTagId.value) ||
    Boolean(searchAgentId.value) ||
    Boolean(searchStatus.value) ||
    Boolean(searchBot.value) ||
    Boolean(searchSince.value) ||
    Boolean(searchUntil.value)
)

// Operators seen in the loaded list; enough for the search filter
// Filled by the online strip; the agent filter also lists them
const allAgents = ref([])
const onlineAgents = computed(() =>
  allAgents.value.filter(agent => agent.availability !== 'offline')
)

// Everyone who could be searched for, not only whoever is on the line now
const knownAgents = computed(() => {
  const map = new Map()
  conversations.value.forEach(c => {
    if (c.assignee) map.set(c.assignee.id, c.assignee.name)
  })
  allAgents.value.forEach(agent => map.set(agent.id, agent.name))
  return [...map.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

// The server stops counting at 999: past that the exact number costs a full
// scan of the archive and tells the operator nothing
const SEARCH_COUNT_CAP = 999
const searchCountLabel = computed(() =>
  searchTotal.value >= SEARCH_COUNT_CAP ? `${SEARCH_COUNT_CAP}+` : searchTotal.value
)

const tagOptions = computed(() =>
  toOptions(tags.value, t('CONVERSATIONS.SEARCH.ANY_TAG'))
)
const agentOptions = computed(() =>
  toOptions(knownAgents.value, t('CONVERSATIONS.SEARCH.ANY_AGENT'))
)
const searchStatusOptions = computed(() =>
  [
    { value: '', label: t('CONVERSATIONS.SEARCH.STATUS.ANY') },
    canSeeAll.value
      ? { value: 'queued', label: t('CONVERSATIONS.SEARCH.STATUS.QUEUED') }
      : null,
    { value: 'open', label: t('CONVERSATIONS.SEARCH.STATUS.OPEN') },
    { value: 'resolved', label: t('CONVERSATIONS.SEARCH.STATUS.RESOLVED') },
  ].filter(Boolean)
)

// Reading the bot's work: what it answered itself, apart from what it passed on
const searchBotOptions = computed(() => [
  { value: '', label: t('CONVERSATIONS.SEARCH.BOT.ANY') },
  { value: 'answered', label: t('CONVERSATIONS.SEARCH.BOT.ANSWERED') },
  { value: 'handed_over', label: t('CONVERSATIONS.SEARCH.BOT.HANDED_OVER') },
])

// A thread found by filters opens where the filters pointed, not at its end:
// the client may have written every day since, and the operator would scroll
const searchAnchor = () => {
  const query = searchQuery.value.trim().toLowerCase()
  const since = searchSince.value ? new Date(`${searchSince.value}T00:00:00`) : null
  const agentId = searchAgentId.value ? Number(searchAgentId.value) : null
  if (!query && !since && !agentId) return null

  return messages.value.find(message => {
    if (query && !(message.content || '').toLowerCase().includes(query)) return false
    if (since && new Date(message.created_at) < since) return false
    if (agentId && message.sender?.id !== agentId) return false
    return true
  })
}

const openSearchResult = async conversation => {
  await selectConversation(conversation.id, { keepPosition: true })
  await nextTick()
  const anchor = searchAnchor()
  if (anchor) scrollToMessage(anchor.id)
  else scrollToBottom({ force: true })
}

// The next portion starts after the last row shown, so the archive moving
// under the operator cannot skip threads
const runSearch = async ({ append = false } = {}) => {
  if (!hasSearchInput.value) {
    searchResults.value = []
    searchTotal.value = 0
    searchHasMore.value = false
    searchDone.value = false
    return
  }
  const last = append ? searchResults.value[searchResults.value.length - 1] : null
  searching.value = true
  try {
    const { data } = await searchConversations(accountId.value, {
      q: searchQuery.value.trim() || undefined,
      inbox_id: inboxFilter.value || undefined,
      tag_id: searchTagId.value || undefined,
      agent_id: searchAgentId.value || undefined,
      status: searchStatus.value || undefined,
      bot: searchBot.value || undefined,
      since: searchSince.value || undefined,
      until: searchUntil.value || undefined,
      before_at: last?.last_activity_at || undefined,
      before_id: last?.id || undefined,
    })
    searchResults.value = append
      ? [...searchResults.value, ...data.conversations]
      : data.conversations
    searchTotal.value = data.total
    searchHasMore.value = data.has_more
    searchDone.value = true
  } catch (error) {
    useAlert(t('CONVERSATIONS.SEARCH.ERROR'))
  } finally {
    searching.value = false
  }
}

watch(
  [
    searchQuery,
    inboxFilter,
    searchTagId,
    searchAgentId,
    searchStatus,
    searchBot,
    searchSince,
    searchUntil,
  ],
  () => {
    saveFilters()
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(runSearch, 350)
  }
)

// Filters can arrive in the url: from monitoring, or from a shared link.
// The panel opens with them, otherwise the narrowed list looks unexplained
const applySearchFromQuery = () => {
  const { agent_id: agentId, since, until, inbox, status } = route.query
  if (agentId || since || until || inbox || status) {
    searchOpen.value = true
    inboxFilter.value = inbox ? Number(inbox) : ''
    searchAgentId.value = agentId || ''
    searchStatus.value = status || ''
    searchSince.value = since || ''
    searchUntil.value = until || ''
    runSearch()
    return
  }

  if (!restoreFilters()) return

  searchOpen.value = true
  runSearch()
}

const toggleSearch = () => {
  searchOpen.value = !searchOpen.value
  if (!searchOpen.value) {
    searchQuery.value = ''
    inboxFilter.value = ''
    searchTagId.value = ''
    searchAgentId.value = ''
    searchStatus.value = ''
    searchBot.value = ''
    searchSince.value = ''
    searchUntil.value = ''
    localStorage.removeItem(FILTERS_KEY)
    searchResults.value = []
    searchTotal.value = 0
    searchHasMore.value = false
    searchDone.value = false
  }
}

const showTransferModal = ref(false)
const transferTargetRef = ref(null)
const transferAssigneeId = ref('')
const transferComment = ref('')
const transferAgents = ref([])
const transferring = ref(false)

// Who is on the line comes first, and every name carries its status dot, so
// a thread is not handed to someone who left an hour ago
const AVAILABILITY_ORDER = { online: 0, busy: 1, offline: 2 }
const AVAILABILITY_DOT = {
  online: 'bg-n-teal-10',
  busy: 'bg-n-amber-10',
  offline: 'bg-n-slate-10',
}
const transferOptions = computed(() => [
  { value: '', label: t('CONVERSATIONS.TRANSFER.TARGET.QUEUE') },
  ...[...transferAgents.value]
    .sort(
      (a, b) =>
        (AVAILABILITY_ORDER[a.availability] ?? 3) -
          (AVAILABILITY_ORDER[b.availability] ?? 3) ||
        a.name.localeCompare(b.name, 'ru')
    )
    .map(agent => ({
      value: agent.id,
      label: agent.name,
      dotClass:
        AVAILABILITY_DOT[agent.availability] ?? AVAILABILITY_DOT.offline,
    })),
])

const openTransfer = async () => {
  transferAssigneeId.value = ''
  transferComment.value = ''
  showTransferModal.value = true
  nextTick(() => transferTargetRef.value?.focus())
  const accountId = currentUser.value?.accounts?.[0]?.id
  const inboxId = selectedConversation.value?.inbox?.id
  if (!accountId || !inboxId) return
  try {
    const { data } = await getAssignableAgents(accountId, inboxId)
    // Cannot transfer to yourself
    transferAgents.value = data.filter(a => a.id !== currentUser.value?.id)
  } catch (error) {
    transferAgents.value = []
  }
}

const submitTransfer = async () => {
  if (transferring.value) return
  transferring.value = true
  try {
    await store.dispatch('conversations/transfer', {
      id: selectedId.value,
      assignee_id: transferAssigneeId.value || null,
      comment: transferComment.value.trim(),
    })
    useAlert(t('CONVERSATIONS.TRANSFER.SUCCESS'))
    showTransferModal.value = false
  } catch (error) {
    useAlert(t('CONVERSATIONS.TRANSFER.ERROR'))
  } finally {
    transferring.value = false
  }
}

// Spec 3.1.5: personal counters — active threads, free slots, time on shift
// Threads are only handed out to operators on the line, so an empty screen is
// where the reminder belongs
const offTheLine = computed(() => currentUser.value?.availability !== 'online')

// The watchdog moves a silent operator to busy; they have to notice it
const steppedAsideOpen = ref(false)
const returningOnline = ref(false)

const returnOnline = async () => {
  returningOnline.value = true
  try {
    await store.dispatch('auth/updateProfile', { availability: 'online' })
    steppedAsideOpen.value = false
  } catch (error) {
    useAlert(t('AVAILABILITY.ERROR'))
  } finally {
    returningOnline.value = false
  }
}

// Switched behind the operator's back — by the watchdog, an administrator or
// a lost session: the profile still says online until it is reloaded
watch(
  () => stats.value?.availability,
  availability => {
    if (!availability || availability === currentUser.value?.availability) return

    if (availability === 'busy') steppedAsideOpen.value = true
    store.dispatch('auth/validityCheck')
  }
)

// Hover preview: last messages of a thread without opening it
const preview = ref(null)
let previewTimer = null
// Hiding waits a moment so the cursor can reach the panel and scroll it
const HIDE_DELAY = 200
let hideTimer = null

const showPreview = (conversation, event) => {
  clearTimeout(previewTimer)
  clearTimeout(hideTimer)
  const rowRect = event.currentTarget.getBoundingClientRect()
  previewTimer = setTimeout(async () => {
    try {
      const { data } = await getMessages(accountId.value, conversation.id, {
        preview: 1,
      })
      preview.value = {
        conversationId: conversation.id,
        name: conversation.contact.name,
        assigneeName: conversation.assignee?.name,
        channelSlug: conversation.inbox?.channel_slug,
        inboxName: conversation.inbox?.name,
        messages: data.filter(m => m.message_type !== 'activity').slice(-10),
        left: rowRect.right + 8,
        top: Math.max(8, Math.min(rowRect.top, window.innerHeight - 340)),
      }
    } catch (error) {
      preview.value = null
    }
  }, 400)
}

const hidePreview = () => {
  clearTimeout(previewTimer)
  clearTimeout(hideTimer)
  hideTimer = setTimeout(() => {
    preview.value = null
  }, HIDE_DELAY)
}

const keepPreview = () => clearTimeout(hideTimer)

const openPreviewConversation = () => {
  const id = preview.value?.conversationId
  if (id) selectConversation(id)
}

// Transfer hotkey: T outside of inputs opens the transfer dialog
const onKeydown = event => {
  if (event.key === 'Escape' && emojiOpen.value) {
    emojiOpen.value = false
    return
  }
  if (event.code !== 'KeyT' || event.metaKey || event.ctrlKey || event.altKey)
    return
  const target = event.target
  if (
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    target.isContentEditable
  )
    return
  if (!selectedConversation.value || showTransferModal.value) return
  event.preventDefault()
  openTransfer()
}

// Compact "who is online" strip for supervisors
const ONLINE_REFRESH_INTERVAL = 30000
let onlineTimer = null

const loadOnlineAgents = async () => {
  if (!canSeeAll.value || !accountId.value) return
  try {
    const { data } = await getMonitoring(accountId.value)
    allAgents.value = data.agents
  } catch (error) {
    // non-fatal; retried by the next refresh
  }
}

// Clicking an operator in the online strip shows what they are handling now
const showAgentThreads = agent => {
  searchOpen.value = true
  searchQuery.value = ''
  inboxFilter.value = ''
  searchTagId.value = ''
  searchSince.value = ''
  searchUntil.value = ''
  searchAgentId.value = agent.id
  searchStatus.value = 'open'
  runSearch()
}

const poll = async () => {
  throttledPingTyping()
  await store.dispatch('conversations/get')
  if (!selectedId.value) return

  const before = messages.value.length
  await store.dispatch('conversations/fetchMessages', selectedId.value)
  if (messages.value.length === before) return
  if (atBottom.value) scrollToBottom()
  else hasNewBelow.value = true
}

// Scheduled after the answer, so slow responses cannot pile up
const scheduleNextPoll = () => {
  clearTimeout(pollTimer)
  pollTimer = setTimeout(runPoll, POLL_INTERVAL)
}

const runPoll = async () => {
  // Runs in the background too: a hidden tab still needs the chime.
  // Chrome throttles background timers, so the alert may lag by a minute.
  try {
    await poll()
  } catch (error) {
    // polling errors are non-fatal, the next round retries
  }
  scheduleNextPoll()
}

// Coming back to the tab should show the current state at once
const onTabVisible = () => {
  if (document.hidden) return

  clearTimeout(pollTimer)
  runPoll()
}

// Realtime: cable events trigger targeted refetches; polling stays as a rare fallback
let cable = null
let listRefreshTimer = null

const refreshListSoon = () => {
  if (listRefreshTimer) return
  listRefreshTimer = setTimeout(() => {
    listRefreshTimer = null
    store.dispatch('conversations/get')
    store.dispatch('stats/fetch')
  }, 300)
}

const onCableEvent = (event, data) => {
  if (event === 'message.created' && data.conversation_id === selectedId.value) {
    store.dispatch('conversations/fetchMessages', selectedId.value)
  }
  if (event === 'message.created' || event === 'conversation.updated') {
    refreshListSoon()
  }
}

onMounted(async () => {
  await store.dispatch('conversations/get')
  restoreFromQuery()
  applySearchFromQuery()
  scheduleNextPoll()
  document.addEventListener('visibilitychange', onTabVisible)
  loadOnlineAgents()
  onlineTimer = setInterval(loadOnlineAgents, ONLINE_REFRESH_INTERVAL)
  window.addEventListener('keydown', onKeydown)
})

// The profile arrives asynchronously, so reference lists wait for the account
watch(
  accountId,
  id => {
    if (!id) return
    loadTags()
    loadInboxes()
  },
  { immediate: true }
)

// The profile loads asynchronously, so connect as soon as the token is there
watch(
  () => Boolean(currentUser.value?.pubsub_token && accountId.value),
  ready => {
    if (!REALTIME_ENABLED || !ready || cable) return
    cable = createCableConsumer({
      pubsubToken: currentUser.value.pubsub_token,
      accountId: accountId.value,
      onEvent: onCableEvent,
    })
  },
  { immediate: true }
)

onUnmounted(() => {
  composerObserver?.disconnect()
  // Leaving the page must not drop what was typed in the last moment
  if (draftTimer) {
    clearTimeout(draftTimer)
    persistDraft(selectedId.value)
  }
  clearTimeout(pollTimer)
  document.removeEventListener('visibilitychange', onTabVisible)
  if (listRefreshTimer) clearTimeout(listRefreshTimer)
  if (onlineTimer) clearInterval(onlineTimer)
  if (previewTimer) clearTimeout(previewTimer)
  if (hideTimer) clearTimeout(hideTimer)
  window.removeEventListener('keydown', onKeydown)
  cable?.disconnect()
})
</script>

<template>
  <div class="flex flex-1 min-h-0 bg-n-background">
    <!-- Conversation list -->
    <aside
      class="flex flex-col w-80 shrink-0 h-full border-r border-n-weak bg-n-solid-1"
    >
      <header class="flex items-center justify-between px-4 h-14 shrink-0">
        <h1 class="text-heading-1 text-n-slate-12">
          {{ $t('CONVERSATIONS.TITLE') }}
        </h1>
        <Button
          sm
          faded
          slate
          :icon="searchOpen ? 'i-lucide-x' : 'i-lucide-search'"
          :aria-label="$t('CONVERSATIONS.SEARCH.BUTTON')"
          @click="toggleSearch"
        />
      </header>

      <!-- Load counters are supervisor material; shift time lives in the top bar -->
      <div
        v-if="canSeeAll && stats && !searchOpen"
        class="flex items-center gap-3 px-4 pb-2 shrink-0 text-xs text-n-slate-11"
      >
        <span :title="$t('CONVERSATIONS.STATS.ACTIVE_TITLE')">
          <span class="i-lucide-message-square-dot inline-block size-3.5 align-[-2px]" />
          {{ stats.active_count }}
        </span>
        <span :title="$t('CONVERSATIONS.STATS.SLOTS_TITLE')">
          <span class="i-lucide-inbox inline-block size-3.5 align-[-2px]" />
          {{ stats.slots_free ?? '∞' }}
        </span>
      </div>

      <div v-if="!searchOpen" class="flex flex-col gap-1.5 px-2 pb-2 shrink-0">
        <div
          class="grid gap-0.5 p-0.5 rounded-lg bg-n-alpha-1"
          :class="canSeeAll ? 'grid-cols-6' : 'grid-cols-2'"
        >
          <button
            v-for="tab in stateTabs"
            :key="tab.key"
            v-tooltip.bottom="tab.title"
            type="button"
            class="flex flex-col items-center justify-center gap-0.5 px-0.5 py-1 rounded-md min-w-0"
            :class="
              stateTab === tab.key
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="stateTab = tab.key"
          >
            <span class="text-sm font-semibold leading-4">
              {{ tab.count >= 999 ? '999+' : tab.count }}
            </span>
            <span class="w-full text-[10px] leading-3 truncate">
              {{ tab.label }}
            </span>
          </button>
        </div>
        <button
          v-if="canSeeAll"
          type="button"
          class="flex items-center self-start gap-1 px-2 py-0.5 text-xs rounded-lg"
          :class="
            onlyMine
              ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="onlyMine = !onlyMine"
        >
          {{ $t('CONVERSATIONS.STATES.MINE_ONLY') }}
        </button>
      </div>

      <div
        v-if="canSeeAll && onlineAgents.length && !searchOpen"
        class="flex items-center gap-2 px-4 pb-2 shrink-0 overflow-x-auto text-xs text-n-slate-11"
      >
        <router-link to="/monitoring" class="shrink-0 hover:text-n-slate-12">
          {{ $t('CONVERSATIONS.ONLINE.TITLE') }}:
        </router-link>
        <button
          v-for="agent in onlineAgents"
          :key="agent.id"
          v-tooltip.top="
            $t('CONVERSATIONS.ONLINE.AGENT', {
              name: agent.name,
              count: agent.active_count,
            })
          "
          type="button"
          class="flex items-center gap-1 px-1 bg-transparent border-0 rounded-lg cursor-pointer shrink-0 hover:bg-n-alpha-2"
          @click="showAgentThreads(agent)"
        >
          <Avatar
            :name="agent.name"
            :size="20"
            :status="agent.availability"
            :src="agent.avatar_url"
            logo-fallback
          />
          <span class="text-n-slate-12">{{ agent.active_count }}</span>
        </button>
      </div>

      <!-- Search mode -->
      <template v-if="searchOpen">
        <div class="flex flex-col gap-2 px-4 pb-2 shrink-0">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('CONVERSATIONS.SEARCH.PLACEHOLDER')"
            class="w-full h-8 px-3 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
          <Select
            v-if="inboxes.length > 1"
            v-model="inboxFilter"
            full-width
            :options="inboxOptions"
          />
          <Select v-model="searchTagId" full-width :options="tagOptions" />
          <Select
            v-if="canSeeAll"
            v-model="searchAgentId"
            full-width
            :options="agentOptions"
          />
          <Select
            v-model="searchStatus"
            full-width
            :options="searchStatusOptions"
          />
          <Select v-model="searchBot" full-width :options="searchBotOptions" />
          <div class="flex items-center gap-2">
            <input
              v-model="searchSince"
              type="date"
              :aria-label="$t('CONVERSATIONS.SEARCH.SINCE')"
              class="flex-1 min-w-0 h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
            />
            <span class="text-n-slate-10">—</span>
            <input
              v-model="searchUntil"
              type="date"
              :aria-label="$t('CONVERSATIONS.SEARCH.UNTIL')"
              class="flex-1 min-w-0 h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
            />
          </div>
        </div>

        <p
          v-if="searchDone && searchResults.length"
          class="px-4 py-2 text-xs border-b text-n-slate-11 border-n-weak"
        >
          {{ $t('CONVERSATIONS.SEARCH.FOUND', { count: searchCountLabel }) }}
        </p>

        <div class="flex-1 min-h-0 overflow-y-auto">
          <p
            v-if="!searchDone && !searching"
            class="py-8 px-6 text-center text-body-main text-n-slate-11"
          >
            {{ $t('CONVERSATIONS.SEARCH.HINT') }}
          </p>
          <p
            v-else-if="searching && !searchResults.length"
            class="py-8 px-6 text-center text-body-main text-n-slate-11"
          >
            {{ $t('CONVERSATIONS.LOADING') }}
          </p>
          <p
            v-else-if="!searchResults.length"
            class="py-8 px-6 text-center text-body-main text-n-slate-11"
          >
            {{ $t('CONVERSATIONS.SEARCH.EMPTY') }}
          </p>
          <ul v-else class="flex flex-col m-0 list-none">
            <li
              v-for="conversation in searchResults"
              :key="conversation.id"
              class="border-b border-n-weak"
            >
              <button
                type="button"
                class="flex w-full items-start gap-3 px-4 py-3 text-left"
                :class="
                  conversation.id === selectedId
                    ? 'bg-n-alpha-2'
                    : 'hover:bg-n-alpha-1'
                "
                @click="openSearchResult(conversation)"
                @mouseenter="showPreview(conversation, $event)"
                @mouseleave="hidePreview"
              >
                <span class="relative shrink-0">
                <Avatar :name="conversation.contact.name || '?'" :size="40" />
                <span
                  v-tooltip.top="conversation.inbox.name"
                  class="absolute -bottom-1 -right-1 grid place-content-center overflow-hidden rounded-full size-5 bg-n-solid-2 outline outline-2 outline-n-background"
                >
                  <ChannelIcon
                    :slug="conversation.inbox.channel_slug"
                    icon-class="size-3"
                  />
                </span>
              </span>
                <div class="flex flex-col flex-1 min-w-0 gap-0.5">
                  <div class="flex items-center justify-between gap-2">
                    <span class="truncate text-heading-3 text-n-slate-12">
                      {{
                        conversation.contact.name || $t('CONVERSATIONS.UNKNOWN')
                      }}
                    </span>
                    <span class="shrink-0 text-xs text-n-slate-10">
                      {{ formatListTime(conversation.last_activity_at) }}
                    </span>
                  </div>
                  <div class="flex items-center justify-between gap-2">
                    <p class="truncate text-body-main text-n-slate-11">
                      №{{ conversation.display_id }} ·
                      {{ previewText(conversation.last_message) || '—' }}
                    </p>
                    <span
                      v-if="conversation.booking_notice"
                      class="shrink-0 px-1.5 py-0.5 text-[10px] rounded-full bg-n-amber-3 text-n-amber-11"
                    >
                      {{ $t('CONVERSATIONS.BOOKING_BADGE') }}
                    </span>
                    <span
                      v-if="conversation.status === 'resolved'"
                      class="shrink-0 px-1.5 py-0.5 text-[10px] rounded-full bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ $t('CONTACT_CARD.STATUS.resolved') }}
                    </span>
                  </div>
                </div>
              </button>
            </li>
          </ul>

          <div v-if="searchHasMore" class="flex justify-center p-3">
            <Button
              sm
              faded
              type="button"
              :label="$t('CONVERSATIONS.SEARCH.MORE')"
              :disabled="searching"
              @click="runSearch({ append: true })"
            />
          </div>
        </div>
      </template>

      <div v-else class="flex-1 min-h-0 overflow-y-auto">
        <div
          v-if="!uiFlags.hasFetched"
          class="flex items-center justify-center py-16 text-body-main text-n-slate-11"
        >
          {{ $t('CONVERSATIONS.LOADING') }}
        </div>

        <div
          v-else-if="!conversations.length"
          class="flex flex-col items-center justify-center gap-2 py-16 px-6 text-center"
        >
          <span class="i-lucide-message-circle size-8 text-n-slate-9" />
          <p class="text-body-main text-n-slate-11">
            {{ $t('CONVERSATIONS.EMPTY.TITLE') }}
          </p>
        </div>

        <div
          v-else-if="!visibleConversations.length"
          class="flex flex-col items-center justify-center gap-2 py-16 px-6 text-center"
        >
          <p class="text-body-main text-n-slate-11">
            {{ $t('CONVERSATIONS.TABS.EMPTY') }}
          </p>
        </div>

        <ul
          v-else
          ref="listRef"
          class="flex flex-col m-0 list-none"
          @keydown.down.prevent="moveSelection(1)"
          @keydown.up.prevent="moveSelection(-1)"
        >
          <li
            v-for="conversation in visibleConversations"
            :key="conversation.id"
            class="border-b border-n-weak"
          >
            <button
              type="button"
              :data-conversation="conversation.id"
              class="flex w-full items-start gap-3 px-4 py-3 text-left"
              :class="
                conversation.id === selectedId
                  ? 'bg-n-alpha-2'
                  : 'hover:bg-n-alpha-1'
              "
              @click="selectConversation(conversation.id)"
              @mouseenter="showPreview(conversation, $event)"
              @mouseleave="hidePreview"
            >
              <span class="relative shrink-0">
                <Avatar :name="conversation.contact.name || '?'" :size="40" />
                <span
                  v-tooltip.top="conversation.inbox.name"
                  class="absolute -bottom-1 -right-1 grid place-content-center overflow-hidden rounded-full size-5 bg-n-solid-2 outline outline-2 outline-n-background"
                >
                  <ChannelIcon
                    :slug="conversation.inbox.channel_slug"
                    icon-class="size-3"
                  />
                </span>
              </span>
              <div class="flex flex-col flex-1 min-w-0 gap-0.5">
                <div class="flex items-center justify-between gap-2">
                  <span
                    class="truncate text-heading-3 text-n-slate-12"
                  >
                    {{ conversation.contact.name || $t('CONVERSATIONS.UNKNOWN') }}
                  </span>
                  <span class="flex items-center gap-1 shrink-0 text-xs text-n-slate-10">
                    <span
                      v-if="conversation.unread_count"
                      class="grid place-content-center min-w-4 h-4 px-1 text-[10px] font-medium rounded-full bg-n-ruby-9 text-white"
                    >
                      {{ conversation.unread_count }}
                    </span>
                    <span
                      v-if="conversation.state === 'waiting_agent'"
                      v-tooltip.top="$t('CONVERSATIONS.STATES.WAITING_AGENT')"
                      class="size-2 rounded-full bg-n-ruby-9"
                    />
                    {{ formatListTime(conversation.last_activity_at) }}
                  </span>
                </div>
                <div class="flex items-center justify-between gap-2">
                  <p class="truncate text-body-main text-n-slate-11">
                    {{
                      previewText(conversation.last_message) ||
                      (conversation.last_message?.has_attachments
                        ? $t('CONVERSATIONS.ATTACHMENTS.PREVIEW')
                        : '—')
                    }}
                  </p>
                  <span
                    v-if="conversation.assignee"
                    class="flex items-center gap-1 shrink-0 max-w-40"
                  >
                    <span
                      v-if="canSeeAll"
                      class="text-xs truncate text-n-slate-11"
                    >
                      {{ conversation.assignee.name }}
                    </span>
                    <Avatar
                      v-tooltip.top="conversation.assignee.name"
                      :name="conversation.assignee.name"
                      :size="16"
                      :src="conversation.assignee.avatar_url"
                      logo-fallback
                      class="shrink-0"
                    />
                  </span>
                </div>
              </div>
            </button>
          </li>
        </ul>
      </div>
    </aside>

    <!-- Hover preview -->
    <div
      v-if="preview"
      class="fixed z-40 flex flex-col w-96 max-h-80 p-3 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg cursor-pointer"
      :style="{ left: `${preview.left}px`, top: `${preview.top}px` }"
      @mouseenter="keepPreview"
      @mouseleave="hidePreview"
      @click="openPreviewConversation"
    >
      <div class="flex flex-col gap-0.5 pb-2 shrink-0">
        <span class="text-heading-3 text-n-slate-12">
          {{ preview.name || $t('CONVERSATIONS.UNKNOWN') }}
        </span>
        <span class="flex items-center gap-1.5 text-xs text-n-slate-11">
          <ChannelIcon
            :slug="preview.channelSlug"
            icon-class="size-3.5"
            image-class="size-3.5"
          />
          {{ preview.inboxName }}
          <span class="text-n-slate-9">·</span>
          <span :class="preview.assigneeName ? '' : 'text-n-slate-10'">
            {{ preview.assigneeName || $t('CONVERSATIONS.PREVIEW.UNASSIGNED') }}
          </span>
        </span>
      </div>
      <p
        v-if="!preview.messages.length"
        class="m-0 text-body-main text-n-slate-11"
      >
        {{ $t('CONVERSATIONS.PREVIEW.EMPTY') }}
      </p>
      <div class="flex flex-col gap-2 min-h-0 overflow-y-auto">
        <div
          v-for="message in preview.messages"
          :key="message.id"
          class="flex"
          :class="
            message.message_type === 'outgoing'
              ? 'justify-end'
              : 'justify-start'
          "
        >
        <div
          class="max-w-[85%] px-2.5 py-1.5 text-xs rounded-xl whitespace-pre-wrap break-words"
          :class="
            message.message_type === 'outgoing'
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-12'
          "
        >
          {{
            message.content ||
            (message.attachments?.length
              ? $t('CONVERSATIONS.ATTACHMENTS.PREVIEW')
              : '')
          }}
          <span
            class="ml-1 text-[10px]"
            :class="
              message.message_type === 'outgoing'
                ? 'text-white/70'
                : 'text-n-slate-10'
            "
          >
            {{ formatTime(message.created_at) }}
          </span>
        </div>
        </div>
      </div>
    </div>

    <!-- Thread -->
    <section class="flex flex-col flex-1 min-w-0 h-full">
      <div
        v-if="!selectedConversation"
        class="flex flex-col items-center justify-center flex-1 gap-3 p-8"
      >
        <span class="i-lucide-messages-square size-10 text-n-slate-9" />
        <p class="text-heading-3 text-n-slate-12">
          {{ $t('CONVERSATIONS.NO_SELECTION.TITLE') }}
        </p>
        <p class="text-body-main text-n-slate-11">
          {{ $t('CONVERSATIONS.NO_SELECTION.DESCRIPTION') }}
        </p>
        <template v-if="offTheLine">
          <p class="text-body-main text-n-slate-11">
            {{ $t('AVAILABILITY.OFF_THE_LINE') }}
          </p>
          <Button
            :label="$t('CONVERSATIONS.STEPPED_ASIDE.BACK_ONLINE')"
            :disabled="returningOnline"
            @click="returnOnline"
          />
        </template>
      </div>

      <template v-else>
        <header
          class="flex items-center justify-between gap-4 px-6 h-14 shrink-0 border-b border-n-weak bg-n-solid-1"
        >
          <div class="flex items-center gap-3 min-w-0">
            <Avatar :name="selectedConversation.contact.name || '?'" :size="32" />
            <div class="flex flex-col min-w-0">
              <span class="truncate text-heading-3 text-n-slate-12">
                {{
                  selectedConversation.contact.name ||
                  $t('CONVERSATIONS.UNKNOWN')
                }}
              </span>
              <span class="flex items-center gap-1 truncate text-xs text-n-slate-11">
                {{ selectedConversation.inbox.name }}
                <span>·</span>
                <button
                  type="button"
                  v-tooltip.bottom="$t('CONVERSATIONS.COPY_HINT')"
                  class="bg-transparent border-0 cursor-pointer text-n-slate-11 hover:text-n-slate-12"
                  @click="
                    copyToClipboard(
                      selectedConversation.display_id,
                      $t('CONVERSATIONS.THREAD_NUMBER_LABEL')
                    )
                  "
                >
                  №{{ selectedConversation.display_id }}
                </button>
                <template v-if="selectedConversation.contact.source_id">
                  <span>·</span>
                  <button
                    type="button"
                    v-tooltip.bottom="$t('CONVERSATIONS.COPY_HINT')"
                    class="truncate bg-transparent border-0 cursor-pointer text-n-slate-11 hover:text-n-slate-12"
                    @click="
                      copyToClipboard(
                        selectedConversation.contact.source_id,
                        $t('CONVERSATIONS.CLIENT_ID_LABEL')
                      )
                    "
                  >
                    {{
                      $t('CONVERSATIONS.CLIENT_ID', {
                        id: selectedConversation.contact.source_id,
                      })
                    }}
                  </button>
                </template>
                <template v-if="selectedConversation.booking_notice">
                  <span>·</span>
                  <a
                    :href="selectedConversation.booking_order_url"
                    target="_blank"
                    rel="noopener"
                    class="truncate text-n-amber-11 hover:underline"
                  >
                    {{
                      $t('CONVERSATIONS.BOOKING_NOTICE', {
                        id: selectedConversation.booking_order_id,
                      })
                    }}
                  </a>
                </template>
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <div class="relative">
              <Button
                sm
                faded
                slate
                icon="i-lucide-tag"
                :label="
                  selectedConversation.tag?.name || $t('CONVERSATIONS.TAG.NONE')
                "
                @click="tagMenuOpen = !tagMenuOpen"
              />
              <div
                v-if="tagMenuOpen"
                class="absolute right-0 z-20 min-w-48 p-1 mt-1 top-full rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-sm"
              >
                <button
                  v-for="tag in tags"
                  :key="tag.id"
                  type="button"
                  class="flex items-center w-full gap-2 p-2 text-sm text-left border-0 rounded-lg whitespace-nowrap text-n-slate-12 hover:bg-n-alpha-2"
                  :disabled="settingTag"
                  @click="setTag(tag.id)"
                >
                  <span
                    v-if="selectedConversation.tag?.id === tag.id"
                    class="i-lucide-check size-3.5 shrink-0 text-n-brand"
                  />
                  <span v-else class="size-3.5 shrink-0" />
                  {{ tag.name }}
                </button>
                <div class="h-px my-1 bg-n-weak" />
                <button
                  type="button"
                  class="flex items-center w-full gap-2 p-2 text-sm text-left border-0 rounded-lg whitespace-nowrap text-n-slate-11 hover:bg-n-alpha-2"
                  :disabled="settingTag"
                  @click="setTag(null)"
                >
                  <span class="size-3.5 shrink-0" />
                  {{ $t('CONVERSATIONS.TAG.CLEAR') }}
                </button>
              </div>
            </div>
            <Button
              v-tooltip.top="$t('CONVERSATIONS.BLOCK.BUTTON')"
              sm
              faded
              slate
              icon="i-lucide-ban"
              class="hover:enabled:text-n-ruby-11"
              :aria-label="$t('CONVERSATIONS.BLOCK.BUTTON')"
              @click="showBlockModal = true"
            />
            <Button
              v-if="canRate"
              sm
              faded
              slate
              icon="i-lucide-star"
              :label="$t('CONVERSATIONS.RATING.BUTTON')"
              @click="openRating"
            />
            <Button
              v-tooltip.top="$t('CONVERSATIONS.TRANSFER.HOTKEY')"
              sm
              faded
              slate
              icon="i-lucide-arrow-right-left"
              :label="$t('CONVERSATIONS.TRANSFER.BUTTON')"
              @click="openTransfer"
            />
            <Button
              sm
              faded
              slate
              :icon="
                selectedConversation.status === 'resolved'
                  ? 'i-lucide-rotate-ccw'
                  : 'i-lucide-check'
              "
              :label="
                selectedConversation.status === 'resolved'
                  ? $t('CONVERSATIONS.STATUS.REOPEN')
                  : $t('CONVERSATIONS.STATUS.RESOLVE')
              "
              :is-loading="changingStatus"
              :disabled="changingStatus"
              @click="toggleStatus"
            />
            <Button
              v-tooltip.top="$t('CONVERSATIONS.EXPORT.BUTTON')"
              sm
              faded
              slate
              icon="i-lucide-download"
              :aria-label="$t('CONVERSATIONS.EXPORT.BUTTON')"
              @click="downloadTranscript"
            />
            <Button
              v-tooltip.top="$t('CONTACT_CARD.BUTTON')"
              sm
              faded
              slate
              icon="i-lucide-panel-right"
              :class="contactCardOpen ? 'text-n-brand' : ''"
              :aria-label="$t('CONTACT_CARD.BUTTON')"
              @click="toggleContactCard"
            />
          </div>
        </header>

        <div
          ref="threadRef"
          class="relative flex flex-col flex-1 min-h-0 gap-3 px-6 py-4 overflow-y-auto"
          @scroll="onThreadScroll"
        >
          <div
            v-for="group in messageGroups"
            :key="group.day"
            class="flex flex-col gap-3"
          >
            <div class="sticky top-0 z-10 flex justify-center py-1">
              <span
                class="px-3 py-1 text-xs rounded-full shadow-sm bg-n-solid-2 text-n-slate-11"
              >
                {{ group.label }}
              </span>
            </div>
            <template
              v-for="{ message, index: messageIndex } in group.items"
              :key="message.id"
            >
            <div
              v-if="opensRatedStretch(message)"
              class="flex justify-center py-2"
            >
              <span
                class="flex items-center gap-1.5 px-3 py-1 text-xs rounded-full border border-dashed border-n-amber-8 text-n-amber-11"
              >
                <span class="i-lucide-star size-3" />
                {{ $t('CONVERSATIONS.RATING.UNRATED_FROM') }}
              </span>
            </div>
          <div
            class="flex flex-col"
            :class="
              message.message_type === 'outgoing'
                ? 'items-end'
                : message.message_type === 'activity'
                  ? 'items-center'
                  : 'items-start'
            "
          >
            <span
              v-if="message.message_type === 'activity'"
              class="px-3 py-1 text-xs text-center rounded-full bg-n-alpha-2 text-n-slate-10"
            >
              {{ message.content }}
              <span class="text-n-slate-9">· {{ formatTime(message.created_at) }}</span>
            </span>
            <div
              v-else-if="message.message_type === 'note'"
              class="flex flex-col w-full gap-1 px-3 py-2 border-l-2 rounded-lg border-n-amber-9 bg-n-amber-3"
            >
              <span class="flex items-center gap-1.5 text-xs text-n-amber-11">
                <span class="i-lucide-sticky-note size-3.5" />
                {{ $t('CONVERSATIONS.NOTE.LABEL') }}
                <span v-if="message.sender">· {{ message.sender.name }}</span>
                <span class="ml-auto">{{ formatTime(message.created_at) }}</span>
              </span>
              <span class="text-sm whitespace-pre-wrap text-n-slate-12">
                {{ message.content }}
              </span>
            </div>
            <template v-else>
              <span
                v-if="senderLabel(messageIndex)"
                class="px-1 mb-0.5 text-xs text-n-slate-11"
              >
                {{ senderLabel(messageIndex) }}
              </span>
              <div
                class="flex items-center gap-1 max-w-[70%] group"
                :class="
                  message.message_type === 'outgoing' ? 'flex-row-reverse' : ''
                "
              >
              <div
                :id="`message-${message.id}`"
                class="flex flex-col min-w-0 gap-1 px-3 py-2 rounded-2xl transition-colors"
                :class="[
                  message.message_type === 'outgoing'
                    ? 'bg-n-brand text-white rounded-br-sm'
                    : 'bg-n-alpha-2 text-n-slate-12 rounded-bl-sm',
                  highlightedMessageId === message.id
                    ? 'outline outline-2 outline-n-brand'
                    : '',
                ]"
              >
              <button
                v-if="message.reply_to"
                type="button"
                class="flex flex-col items-start gap-0.5 py-0.5 pl-2 pr-0 text-left bg-transparent border-0 border-l-2 cursor-pointer"
                :class="
                  message.message_type === 'outgoing'
                    ? 'border-white/60'
                    : 'border-n-brand'
                "
                :disabled="!message.reply_to.id"
                @click="scrollToMessage(message.reply_to.id)"
              >
                <span
                  class="text-xs font-medium"
                  :class="
                    message.message_type === 'outgoing'
                      ? 'text-white/80'
                      : 'text-n-slate-11'
                  "
                >
                  <template v-if="!message.reply_to.id">
                    {{ $t('CONVERSATIONS.QUOTE.ELSEWHERE') }}
                  </template>
                  <template v-else-if="message.reply_to.message_type === 'outgoing'">
                    {{
                      message.reply_to.sender_name ||
                      $t('CONVERSATIONS.QUOTE.OPERATOR')
                    }}
                  </template>
                  <template v-else>
                    {{ selectedConversation?.contact?.name }}
                  </template>
                </span>
                <span
                  class="text-xs truncate max-w-52"
                  :class="
                    message.message_type === 'outgoing'
                      ? 'text-white/70'
                      : 'text-n-slate-10'
                  "
                >
                  {{ quotePreview(message.reply_to) }}
                </span>
              </button>
              <template
                v-for="attachment in message.attachments || []"
                :key="attachment.id"
              >
                <button
                  v-if="attachment.file_type === 'image'"
                  type="button"
                  @click="galleryStartId = attachment.id"
                >
                  <img
                    :src="attachment.data_url"
                    :alt="attachment.file_name || ''"
                    class="max-w-60 max-h-60 rounded-lg object-cover"
                  />
                </button>
                <button
                  v-else-if="
                    ['video', 'audio'].includes(attachment.file_type)
                  "
                  type="button"
                  class="flex items-center gap-2 text-sm underline underline-offset-2"
                  :class="
                    message.message_type === 'outgoing'
                      ? 'text-white'
                      : 'text-n-slate-12'
                  "
                  @click="galleryStartId = attachment.id"
                >
                  <span class="i-lucide-play size-4 shrink-0" />
                  <span class="truncate max-w-52">
                    {{
                      attachment.file_name ||
                      $t('CONVERSATIONS.ATTACHMENTS.FILE')
                    }}
                  </span>
                </button>
                <a
                  v-else
                  :href="attachment.data_url"
                  target="_blank"
                  rel="noopener"
                  class="flex items-center gap-2 text-sm underline underline-offset-2"
                  :class="
                    message.message_type === 'outgoing'
                      ? 'text-white'
                      : 'text-n-slate-12'
                  "
                >
                  <span class="i-lucide-paperclip size-4 shrink-0" />
                  <span class="truncate max-w-52">
                    {{
                      attachment.file_name ||
                      $t('CONVERSATIONS.ATTACHMENTS.FILE')
                    }}
                  </span>
                </a>
              </template>
              <p
                v-if="message.content"
                class="text-sm whitespace-pre-wrap break-words"
              >
                <template
                  v-for="(part, partIndex) in messageParts(message)"
                  :key="partIndex"
                >
                  <a
                    v-if="part.href"
                    :href="part.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="underline underline-offset-2"
                    :class="part.bold ? 'font-semibold' : ''"
                    @click.stop
                  >
                    {{ part.text }}
                  </a>
                  <strong v-else-if="part.bold">{{ part.text }}</strong>
                  <template v-else>{{ part.text }}</template>
                </template>
              </p>
              <span
                class="flex items-center gap-1 text-[10px] self-end"
                :class="
                  message.message_type === 'outgoing'
                    ? 'text-white/70'
                    : 'text-n-slate-10'
                "
              >
                <span
                  v-if="message.status === 'failed'"
                  v-tooltip.top="
                    message.delivery_error
                      ? $t('CONVERSATIONS.DELIVERY.FAILED_REASON', {
                          reason: message.delivery_error,
                        })
                      : $t('CONVERSATIONS.DELIVERY.FAILED')
                  "
                  class="flex items-center gap-0.5 text-n-ruby-4"
                >
                  <span class="i-lucide-alert-circle size-3" />
                  {{ $t('CONVERSATIONS.DELIVERY.FAILED_SHORT') }}
                </span>
                <span v-if="message.edited_at">
                  {{ $t('CONVERSATIONS.EDIT.LABEL') }} ·
                </span>
                {{ formatTime(message.created_at) }}
              </span>
              </div>
              <button
                v-if="message.quotable"
                v-tooltip.top="$t('CONVERSATIONS.QUOTE.REPLY')"
                type="button"
                class="grid transition-opacity border-0 rounded-full opacity-0 shrink-0 place-content-center size-7 bg-n-alpha-2 group-hover:opacity-100 hover:bg-n-alpha-3"
                :aria-label="$t('CONVERSATIONS.QUOTE.REPLY')"
                @click="startReply(message)"
              >
                <span class="i-lucide-corner-up-left size-3.5 text-n-slate-11" />
              </button>
              <button
                v-if="canEdit(message)"
                v-tooltip.top="$t('CONVERSATIONS.EDIT.ACTION')"
                type="button"
                class="grid transition-opacity border-0 rounded-full opacity-0 shrink-0 place-content-center size-7 bg-n-alpha-2 group-hover:opacity-100 hover:bg-n-alpha-3"
                :aria-label="$t('CONVERSATIONS.EDIT.ACTION')"
                @click="startEdit(message)"
              >
                <span class="i-lucide-pencil size-3.5 text-n-slate-11" />
              </button>
              </div>
            <div
              v-if="clientMark(message)"
              class="flex flex-col items-center gap-1 py-2"
            >
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-1 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
                @click="toggleClientMark(message.thread_id)"
              >
                <span>{{ CLIENT_FACES[clientMark(message).rating - 1] }}</span>
                {{
                  $t('CONVERSATIONS.RATING.CLIENT_CHIP', {
                    score: clientMark(message).rating,
                    who:
                      clientMark(message).about === 'operator'
                        ? clientMark(message).operator_name
                        : $t('CONVERSATIONS.BOT_NAME'),
                  })
                }}
                <span
                  v-if="clientMark(message).reasons.length || clientMark(message).comment || clientMark(message).called_operator"
                  class="size-3"
                  :class="
                    openClientMark === message.thread_id
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                />
              </button>
              <div
                v-if="openClientMark === message.thread_id"
                class="flex flex-col w-full gap-1 px-3 py-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
              >
                <span v-if="clientMark(message).reasons.length">
                  <b>{{ $t('CONVERSATIONS.RATING.CLIENT_REASONS') }}</b>
                  {{ clientMark(message).reasons.join(', ') }}
                </span>
                <span v-if="clientMark(message).comment">
                  <b>{{ $t('CONVERSATIONS.RATING.CLIENT_COMMENT') }}</b>
                  {{ clientMark(message).comment }}
                </span>
                <span v-if="clientMark(message).called_operator">
                  {{ $t('CONVERSATIONS.RATING.CLIENT_CALLED') }}
                </span>
              </div>
            </div>
            <div
              v-if="ratingChip(message)"
              class="flex flex-col items-center gap-1 py-2"
            >
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-1 text-xs rounded-full bg-n-amber-3 text-n-amber-11"
                @click="toggleRatingDetails(message.thread_id)"
              >
                <span class="i-lucide-star size-3" />
                {{
                  $t('CONVERSATIONS.RATING.CHIP', {
                    score: ratingChip(message).rating,
                    author: ratingChip(message).author,
                  })
                }}
                <span
                  class="size-3"
                  :class="
                    openRatingDetails === message.thread_id
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                />
              </button>
              <div
                v-if="openRatingDetails === message.thread_id"
                class="flex flex-col w-full gap-1 px-3 py-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11"
              >
                <span>
                  <b>{{ $t('CONVERSATIONS.RATING.MISTAKE') }}</b>
                  {{ ratingChip(message).mistake }}
                </span>
                <span>
                  <b>{{ $t('CONVERSATIONS.RATING.CORRECTION') }}</b>
                  {{ ratingChip(message).correction }}
                </span>
              </div>
            </div>
            </template>
          </div>
            </template>
          </div>

          <p
            v-if="selectedConversation?.bot_thinking"
            class="self-center px-3 py-1 text-xs rounded-full text-n-slate-11 bg-n-alpha-2"
          >
            {{ $t('CONVERSATIONS.BOT_THINKING', { bot: botName }) }}
          </p>

          <button
            v-if="hasNewBelow"
            type="button"
            class="sticky z-10 flex items-center self-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white rounded-full shadow-md bottom-2 bg-n-brand"
            @click="scrollToBottom({ force: true })"
          >
            <span class="i-lucide-arrow-down size-3" />
            {{ $t('CONVERSATIONS.NEW_BELOW') }}
          </button>
        </div>

        <footer
          class="relative px-6 py-4 shrink-0 border-t border-n-weak bg-n-solid-1"
          @dragover.prevent
          @drop.prevent="onDrop"
        >
          <EmojiPicker
            v-if="emojiOpen"
            class="absolute bottom-full left-6 z-20 mb-1"
            @select="insertEmoji"
          />

          <div
            v-if="cannedOpen"
            class="absolute bottom-full left-6 right-6 z-20 mb-1 p-2 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-sm"
          >
            <div v-if="!cannedFormOpen" class="flex flex-col gap-2">
              <input
                v-model="cannedQuery"
                type="text"
                :placeholder="$t('CONVERSATIONS.CANNED.SEARCH')"
                class="w-full h-8 px-3 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
              />
              <div class="max-h-64 overflow-y-auto">
                <p
                  v-if="!filteredCanned.length"
                  class="py-4 text-center text-body-main text-n-slate-11"
                >
                  {{ $t('CONVERSATIONS.CANNED.EMPTY') }}
                </p>
                <div
                  v-for="response in filteredCanned"
                  :key="response.id"
                  class="flex items-start gap-2 group"
                >
                  <button
                    type="button"
                    class="flex flex-col flex-1 min-w-0 gap-0.5 p-2 text-left border-0 rounded-lg hover:bg-n-alpha-2"
                    @click="insertCanned(response)"
                  >
                    <span class="flex items-center gap-2 text-heading-3 text-n-slate-12">
                      {{ response.title }}
                      <span
                        v-if="response.shared"
                        class="px-1.5 py-0.5 text-[10px] rounded-full bg-n-alpha-2 text-n-slate-11"
                      >
                        {{ $t('CONVERSATIONS.CANNED.SHARED') }}
                      </span>
                    </span>
                    <span class="truncate w-full text-body-main text-n-slate-11">
                      {{ response.content }}
                    </span>
                  </button>
                  <span
                    v-if="canEditCanned(response)"
                    class="flex items-center gap-0.5 mt-2 invisible group-hover:visible"
                  >
                    <Button
                      slate
                      xs
                      icon="i-lucide-pencil"
                      :aria-label="$t('CONVERSATIONS.CANNED.EDIT')"
                      @click="openCannedForm(response)"
                    />
                    <Button
                      slate
                      xs
                      icon="i-mk-bin"
                      class="hover:enabled:text-n-ruby-11"
                      :aria-label="$t('CONVERSATIONS.CANNED.DELETE')"
                      @click="cannedDeleteTarget = response"
                    />
                  </span>
                </div>
              </div>
              <Button
                sm
                faded
                slate
                icon="i-lucide-plus"
                :label="$t('CONVERSATIONS.CANNED.ADD')"
                @click="openCannedForm()"
              />
            </div>

            <form
              v-else
              class="flex flex-col gap-2"
              @submit.prevent="submitCanned"
            >
              <input
                v-model="cannedForm.title"
                type="text"
                :placeholder="$t('CONVERSATIONS.CANNED.FORM.TITLE')"
                class="w-full h-8 px-3 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
              />
              <textarea
                v-model="cannedForm.content"
                rows="3"
                :placeholder="$t('CONVERSATIONS.CANNED.FORM.CONTENT')"
                class="w-full px-3 py-2 text-sm border-none rounded-lg resize-none outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
              />
              <label
                v-if="canManageSharedCanned && !cannedForm.id"
                class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
              >
                <input
                  v-model="cannedForm.shared"
                  type="checkbox"
                  class="w-4 h-4 accent-n-brand"
                />
                {{ $t('CONVERSATIONS.CANNED.FORM.SHARED') }}
              </label>
              <div class="flex justify-end gap-2">
                <Button
                  sm
                  faded
                  slate
                  type="button"
                  :label="$t('CONVERSATIONS.CANNED.FORM.CANCEL')"
                  @click="cannedFormOpen = false"
                />
                <Button
                  sm
                  type="submit"
                  :label="$t('CONVERSATIONS.CANNED.FORM.SUBMIT')"
                  :disabled="savingCanned"
                />
              </div>
            </form>
          </div>

          <div
            v-if="draftFiles.length || draftSaving"
            class="flex flex-wrap items-center gap-2 pb-2"
          >
            <span
              v-for="file in draftFiles"
              :key="file.id"
              class="flex items-center gap-1.5 pl-2 pr-1 py-1 text-xs rounded-lg bg-n-alpha-2 text-n-slate-12"
            >
              <span class="i-lucide-paperclip size-3 text-n-slate-11" />
              <span class="max-w-40 truncate">{{ file.file_name }}</span>
              <button
                type="button"
                class="grid place-content-center size-4 rounded hover:bg-n-alpha-2"
                :aria-label="$t('CONVERSATIONS.ATTACHMENTS.REMOVE')"
                @click="removeFile(file)"
              >
                <span class="i-lucide-x size-3 text-n-slate-11" />
              </button>
            </span>
            <span v-if="draftSaving" class="text-xs text-n-slate-11">
              {{ $t('CONVERSATIONS.DRAFT.UPLOADING') }}
            </span>
          </div>

          <div class="flex gap-1">
            <button
              v-for="mode in [false, true]"
              :key="mode"
              type="button"
              class="px-3 py-1 text-xs border-0 rounded-t-lg cursor-pointer"
              :class="
                noteMode === mode
                  ? 'bg-n-alpha-black2 text-n-slate-12'
                  : 'bg-transparent text-n-slate-11'
              "
              @click="noteMode = mode"
            >
              {{
                mode
                  ? $t('CONVERSATIONS.NOTE.TAB')
                  : $t('CONVERSATIONS.REPLY.TAB')
              }}
            </button>
          </div>

          <form
            class="flex flex-col rounded-xl border overflow-hidden"
            :class="
              noteMode
                ? 'border-n-amber-9 bg-n-amber-2'
                : 'border-n-weak bg-n-alpha-black2'
            "
            @submit.prevent="submitComposer"
          >
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden"
              @change="onFilePick"
            />
            <div
              v-if="editingMessage"
              class="flex items-center gap-2 px-3 pt-2 text-xs"
            >
              <span class="i-lucide-pencil size-3.5 text-n-slate-11 shrink-0" />
              <span class="font-medium text-n-slate-11">
                {{ $t('CONVERSATIONS.EDIT.TITLE') }}
              </span>
              <button
                type="button"
                class="grid ml-auto rounded shrink-0 place-content-center size-5 hover:bg-n-alpha-2"
                :aria-label="$t('CONVERSATIONS.EDIT.CANCEL')"
                @click="cancelEdit"
              >
                <span class="i-lucide-x size-3.5 text-n-slate-11" />
              </button>
            </div>
            <div
              v-else-if="replyTo"
              class="flex items-center gap-2 px-3 pt-2 text-xs"
            >
              <span class="i-lucide-corner-up-left size-3.5 text-n-slate-11 shrink-0" />
              <button
                type="button"
                class="flex flex-col items-start min-w-0 gap-0.5 p-0 text-left bg-transparent border-0 cursor-pointer"
                @click="scrollToMessage(replyTo.id)"
              >
                <span class="font-medium text-n-slate-11">
                  {{
                    replyTo.message_type === 'outgoing'
                      ? replyTo.sender?.name || $t('CONVERSATIONS.QUOTE.OPERATOR')
                      : selectedConversation?.contact?.name
                  }}
                </span>
                <span class="truncate max-w-[32rem] text-n-slate-10">
                  {{ quotePreview(replyTo) }}
                </span>
              </button>
              <button
                type="button"
                class="grid ml-auto rounded shrink-0 place-content-center size-5 hover:bg-n-alpha-2"
                :aria-label="$t('CONVERSATIONS.QUOTE.CANCEL')"
                @click="replyTo = null"
              >
                <span class="i-lucide-x size-3.5 text-n-slate-11" />
              </button>
            </div>
            <textarea
              ref="replyInput"
              v-model="composerText"
              :placeholder="
                noteMode
                  ? $t('CONVERSATIONS.NOTE.PLACEHOLDER')
                  : $t('CONVERSATIONS.REPLY.PLACEHOLDER')
              "
              class="w-full min-h-24 px-3 py-2 text-sm bg-transparent border-none resize-none outline-none text-n-slate-12 placeholder:text-n-slate-10"
              @paste="onPaste"
              @keydown="onReplyKeydown"
              @keydown.esc="
                emojiOpen
                  ? (emojiOpen = false)
                  : editingMessage
                    ? cancelEdit()
                    : (replyTo = null)
              "
            />

            <div class="flex items-center justify-between gap-2 px-2 pb-2">
              <div class="flex items-center gap-1">
                <Button
                  v-if="!noteMode"
                  slate
                  faded
                  sm
                  type="button"
                  icon="i-lucide-paperclip"
                  :aria-label="$t('CONVERSATIONS.ATTACHMENTS.BUTTON')"
                  @click="fileInput.click()"
                />
                <Button
                  slate
                  faded
                  sm
                  type="button"
                  icon="i-lucide-zap"
                  :aria-label="$t('CONVERSATIONS.CANNED.BUTTON')"
                  @click="toggleCanned"
                />
                <Button
                  slate
                  faded
                  sm
                  type="button"
                  icon="i-lucide-smile"
                  :aria-label="$t('EMOJI_PICKER.BUTTON')"
                  @click="toggleEmoji"
                />
              </div>
              <Button
                v-tooltip.top="sendHotKeyHint"
                type="submit"
                :label="$t('CONVERSATIONS.REPLY.SEND')"
                icon="i-lucide-send"
                :disabled="uiFlags.isSending || draftSaving || composerEmpty"
              />
            </div>
          </form>
        </footer>
      </template>
    </section>

    <!-- Client card -->
    <aside
      v-if="contactCardOpen && (selectedConversation || contactCard)"
      class="flex flex-col w-80 shrink-0 h-full border-l border-n-weak bg-n-solid-1 overflow-y-auto"
    >
      <header class="flex items-center justify-between px-4 h-14 shrink-0">
        <h2 class="text-heading-2 text-n-slate-12">
          {{ $t('CONTACT_CARD.TITLE') }}
        </h2>
        <Button
          sm
          faded
          slate
          icon="i-lucide-x"
          :aria-label="$t('CONTACT_CARD.CLOSE')"
          @click="contactCardOpen = false"
        />
      </header>

      <div v-if="contactCard" class="flex flex-col gap-5 px-4 pb-6">
        <div class="flex flex-col items-center gap-2 text-center">
          <Avatar :name="contactCard.name || '?'" :size="64" />
          <div class="flex items-center gap-2">
            <span class="text-heading-2 text-n-slate-12">
              {{ contactCard.name || $t('CONVERSATIONS.UNKNOWN') }}
            </span>
            <span
              v-if="contactCard.blocked"
              class="px-1.5 py-0.5 text-[10px] rounded-full bg-n-ruby-3 text-n-ruby-11"
            >
              {{ $t('CONTACT_CARD.BLOCKED') }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11">
            {{
              $t('CONTACT_CARD.SINCE', {
                date: sinceFormatter.format(new Date(contactCard.first_seen_at)),
              })
            }}
          </span>
        </div>

        <div class="flex flex-col gap-2">
          <div
            v-for="channel in contactCard.channels"
            :key="channel.name"
            class="flex items-start gap-2 text-sm text-n-slate-12"
          >
            <span class="i-lucide-inbox size-4 shrink-0 mt-0.5 text-n-slate-11" />
            <span class="flex flex-col min-w-0">
              {{ channel.name }}
              <button
                v-if="channel.source_id"
                type="button"
                v-tooltip.left="$t('CONVERSATIONS.COPY_HINT')"
                class="text-xs text-left break-all bg-transparent border-0 cursor-pointer text-n-slate-11 hover:text-n-slate-12"
                @click="
                  copyToClipboard(
                    channel.source_id,
                    $t('CONVERSATIONS.CLIENT_ID_LABEL')
                  )
                "
              >
                {{ $t('CONVERSATIONS.CLIENT_ID', { id: channel.source_id }) }}
              </button>
            </span>
          </div>
          <a
            v-if="contactCard.username"
            :href="`https://t.me/${contactCard.username}`"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-2 text-sm text-n-brand hover:underline"
          >
            <span class="i-lucide-at-sign size-4" />
            {{ contactCard.username }}
          </a>
        </div>

        <div class="flex flex-col gap-1.5">
          <h3 class="m-0 text-xs font-medium tracking-wide uppercase text-n-slate-10">
            {{ $t('CONTACT_CARD.HISTORY') }}
          </h3>
          <p
            v-if="!contactCard.conversations.length"
            class="py-2 text-body-main text-n-slate-11"
          >
            {{ $t('CONTACT_CARD.HISTORY_EMPTY') }}
          </p>
          <button
            v-for="item in contactCard.conversations"
            :key="item.id"
            type="button"
            class="flex flex-col gap-1 p-2 text-left border-0 rounded-lg cursor-pointer"
            :class="item.id === selectedId ? 'bg-n-alpha-2' : 'hover:bg-n-alpha-1'"
            @click="selectConversation(item.id)"
          >
            <span class="flex items-center justify-between w-full gap-2">
              <span
                v-tooltip.top="$t('CONVERSATIONS.COPY_HINT')"
                class="text-heading-3 text-n-slate-12 hover:text-n-brand"
                @click.stop="
                  copyToClipboard(
                    item.display_id,
                    $t('CONVERSATIONS.THREAD_NUMBER_LABEL')
                  )
                "
              >
                №{{ item.display_id }}
              </span>
              <span
                class="text-xs"
                :class="
                  item.status === 'open' ? 'text-n-teal-11' : 'text-n-slate-10'
                "
              >
                {{ $t(`CONTACT_CARD.STATUS.${item.status}`) }}
              </span>
            </span>
            <span class="flex items-center justify-between w-full gap-2 text-xs text-n-slate-11">
              <span class="truncate">
                {{ item.assignee_name || $t('CONTACT_CARD.NO_ASSIGNEE') }}
              </span>
              <span class="shrink-0">
                {{ formatListTime(item.last_activity_at || item.created_at) }}
              </span>
            </span>
            <span
              v-if="item.tag_name"
              class="self-start px-1.5 py-0.5 text-[10px] rounded-full bg-n-alpha-2 text-n-slate-11"
            >
              {{ item.tag_name }}
            </span>
          </button>
        </div>
      </div>

      <div
        v-else
        class="flex items-center justify-center py-16 text-body-main text-n-slate-11"
      >
        {{ $t('CONVERSATIONS.LOADING') }}
      </div>
    </aside>

    <Modal
      :show="showBlockModal"
      :title="$t('CONVERSATIONS.BLOCK.TITLE')"
      @close="showBlockModal = false"
    >
      <div class="flex flex-col gap-6">
        <p class="text-body-main text-n-slate-11">
          {{
            canBlockDirectly
              ? $t('CONVERSATIONS.BLOCK.CONFIRM_ADMIN', {
                  name: selectedConversation?.contact?.name,
                })
              : $t('CONVERSATIONS.BLOCK.CONFIRM_AGENT', {
                  name: selectedConversation?.contact?.name,
                })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            :label="$t('CONVERSATIONS.BLOCK.CANCEL')"
            @click="showBlockModal = false"
          />
          <Button
            ruby
            :label="
              canBlockDirectly
                ? $t('CONVERSATIONS.BLOCK.SUBMIT_ADMIN')
                : $t('CONVERSATIONS.BLOCK.SUBMIT_AGENT')
            "
            :disabled="blocking"
            @click="submitBlock"
          />
        </div>
      </div>
    </Modal>

    <Modal
      :show="Boolean(cannedDeleteTarget)"
      :title="$t('CONVERSATIONS.CANNED.DELETE')"
      @close="cannedDeleteTarget = null"
    >
      <div class="flex flex-col gap-6 p-6">
        <p class="text-body-main text-n-slate-11">
          {{
            $t('CONVERSATIONS.CANNED.CONFIRM_DELETE', {
              title: cannedDeleteTarget?.title,
            })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            faded
            slate
            :label="$t('CONVERSATIONS.CANNED.FORM.CANCEL')"
            @click="cannedDeleteTarget = null"
          />
          <Button
            ruby
            :label="$t('CONVERSATIONS.CANNED.DELETE')"
            @click="confirmCannedDelete"
          />
        </div>
      </div>
    </Modal>

    <Modal
      :show="showRatingModal"
      :title="$t('CONVERSATIONS.RATING.TITLE', { bot: botName })"
      :description="$t('CONVERSATIONS.RATING.DESC')"
      @close="showRatingModal = false"
    >
      <form class="flex flex-col gap-5" @submit.prevent="submitRating">
        <p class="text-sm text-n-slate-11">
          {{ $t('CONVERSATIONS.RATING.SCOPE', ratingScope) }}
        </p>
        <div class="space-y-1">
          <span class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ $t('CONVERSATIONS.RATING.SCORE') }}
          </span>
          <div class="flex gap-1">
            <button
              v-for="score in RATING_SCALE"
              :key="score"
              type="button"
              class="w-10 h-10 text-sm font-medium rounded-xl"
              :class="
                rating === score
                  ? 'bg-n-brand text-white'
                  : 'bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="rating = score"
            >
              {{ score }}
            </button>
          </div>
        </div>

        <div class="space-y-1">
          <label for="rating_mistake" class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ $t('CONVERSATIONS.RATING.MISTAKE') }}
          </label>
          <textarea
            id="rating_mistake"
            v-model="ratingMistake"
            rows="3"
            :required="rating !== 5"
            :placeholder="$t('CONVERSATIONS.RATING.MISTAKE_PLACEHOLDER')"
            class="block w-full px-3 py-2.5 text-sm border-none rounded-xl resize-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>

        <div class="space-y-1">
          <label for="rating_correction" class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ $t('CONVERSATIONS.RATING.CORRECTION') }}
          </label>
          <textarea
            id="rating_correction"
            v-model="ratingCorrection"
            rows="3"
            :required="rating !== 5"
            :placeholder="$t('CONVERSATIONS.RATING.CORRECTION_PLACEHOLDER')"
            class="block w-full px-3 py-2.5 text-sm border-none rounded-xl resize-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <Button
            faded
            slate
            type="button"
            :label="$t('CONVERSATIONS.RATING.CANCEL')"
            @click="showRatingModal = false"
          />
          <Button
            type="submit"
            :label="$t('CONVERSATIONS.RATING.SUBMIT')"
            :disabled="savingRating"
          />
        </div>
      </form>
    </Modal>

    <Modal
      :show="showTransferModal"
      :title="$t('CONVERSATIONS.TRANSFER.TITLE')"
      :description="$t('CONVERSATIONS.TRANSFER.DESC')"
      @close="showTransferModal = false"
    >
      <form class="flex flex-col gap-5" @submit.prevent="submitTransfer">
        <div class="space-y-1">
          <label
            for="transfer_target"
            class="mb-0.5 text-heading-3 text-n-slate-12"
          >
            {{ $t('CONVERSATIONS.TRANSFER.TARGET.LABEL') }}
          </label>
          <Select
            id="transfer_target"
            ref="transferTargetRef"
            v-model="transferAssigneeId"
            full-width
            searchable
            :search-placeholder="$t('CONVERSATIONS.TRANSFER.TARGET.SEARCH')"
            :options="transferOptions"
          />
        </div>

        <div class="space-y-1">
          <label
            for="transfer_comment"
            class="mb-0.5 text-heading-3 text-n-slate-12"
          >
            {{ $t('CONVERSATIONS.TRANSFER.COMMENT.LABEL') }}
          </label>
          <textarea
            id="transfer_comment"
            v-model="transferComment"
            rows="3"
            :placeholder="$t('CONVERSATIONS.TRANSFER.COMMENT.PLACEHOLDER')"
            class="block w-full px-3 py-2.5 text-sm border-none rounded-xl resize-none outline outline-1 outline-offset-[-1px] outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
          />
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <Button
            faded
            slate
            type="button"
            :label="$t('CONVERSATIONS.TRANSFER.CANCEL')"
            @click="showTransferModal = false"
          />
          <Button
            type="submit"
            :label="$t('CONVERSATIONS.TRANSFER.SUBMIT')"
            :disabled="transferring"
          />
        </div>
      </form>
    </Modal>

    <Modal
      :show="tagPromptOpen"
      :title="$t('CONVERSATIONS.TAG.PROMPT.TITLE')"
      :description="$t('CONVERSATIONS.TAG.PROMPT.DESC')"
      @close="tagPromptOpen = false"
    >
      <div class="flex flex-col gap-5">
        <Select
          v-model="promptTagId"
          full-width
          searchable
          :search-placeholder="$t('CONVERSATIONS.TAG.PROMPT.SEARCH')"
          :options="tagPromptOptions"
        />
        <div class="flex justify-end gap-2">
          <Button
            slate
            faded
            :label="$t('CONVERSATIONS.TAG.PROMPT.SKIP')"
            @click="tagPromptOpen = false"
          />
          <Button
            :label="$t('CONVERSATIONS.TAG.PROMPT.SAVE')"
            :disabled="!promptTagId || settingTag"
            @click="setTag(promptTagId)"
          />
        </div>
      </div>
    </Modal>

    <Modal
      :show="steppedAsideOpen"
      :title="$t('CONVERSATIONS.STEPPED_ASIDE.TITLE')"
      :description="$t('CONVERSATIONS.STEPPED_ASIDE.DESCRIPTION')"
      @close="steppedAsideOpen = false"
    >
      <div class="flex justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('CONVERSATIONS.STEPPED_ASIDE.DISMISS')"
          @click="steppedAsideOpen = false"
        />
        <Button
          :label="$t('CONVERSATIONS.STEPPED_ASIDE.BACK_ONLINE')"
          :disabled="returningOnline"
          @click="returnOnline"
        />
      </div>
    </Modal>

    <GalleryView
      v-if="galleryStartId"
      :items="galleryItems"
      :start-id="galleryStartId"
      @close="galleryStartId = null"
    />
  </div>
</template>
